import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMusic } from './hooks/useMusic';
import { playSoundEffect } from './hooks/useSoundEffects';
import { isExploreUnknownKendrexColonySilentAfterBattleMissionIndex } from './config/exploreUnknownKendrexColony';
import { setExploreUnknownKendrexColonyBattleAudioSuppressed } from './utils/exploreUnknownKendrexColonyAudio';
import OrderDesign from './OrderDesign';
import { OrderCardBubbles } from './components/OrderCardBubbles';
import { getAvatarImage } from './avatars/avatarManager';
import { useContainerScale } from './hooks/useViewportScale';
import './StrategyStyles.css';
import './AfterBattle.css';
import './afterBattleMobile.css';
import { API_URL } from './config/apiConfig';
import { buildOrderDetailLangQuery } from './utils/buildOrderDetailLangQuery';
import { enrichOrdersWithLocalizedNames } from './utils/enrichOrdersWithLocalizedNames';
import { createPortal } from 'react-dom';
import { TutorialMessageBox } from './tutorial/index';
import { buildFlashlightSwatchBackground, normalizeFlashlightColorForDisplay } from './constants/flashlightColors';
import PvpRankProgressScreen from './pvp/PvpRankProgressScreen';
import { useRematchOptional } from './pvp/RematchProvider';
import { advanceDevMockPvpWin, GENERAL_RANK_INDEX } from './pvp/devPvpLadderMock';
import { isTrainPvEBattle } from './utils/isTrainPvEBattle';
import { isEliteForceJoinedCountryBattle } from './utils/isEliteForceJoinedCountryBattle';
import ExploreUnknownRunRewardsOverview from './ExploreUnknownRunRewardsOverview';
import ExploreUnknownXpGainOverlay from './ExploreUnknownXpGainOverlay';
import './ExploreUnknownXpGainOverlay.css';
import ExploreUnknownMilestoneUnlockOverlay from './ExploreUnknownMilestoneUnlockOverlay';
import ExploreUnknownDarkKnowledgeRewardModal from './exploreUnknownRewardPopups/ExploreUnknownDarkKnowledgeRewardModal';
import ExploreUnknownRewriteFragmentMapProcModal from './exploreUnknownRewardPopups/ExploreUnknownRewriteFragmentMapProcModal';
import ExploreUnknownEpFragmentMapProcModal from './exploreUnknownRewardPopups/ExploreUnknownEpFragmentMapProcModal';
import ExploreUnknownRefinedErrayBossDropModal from './exploreUnknownRewardPopups/ExploreUnknownRefinedErrayBossDropModal';
import './exploreUnknownRewardPopups/ExploreUnknownRewardPopups.css';
import { EXPLORE_UNKNOWN_REWRITE_EXPERIENCE_TARGET_PARTS } from './config/exploreUnknownRewriteExperience';
import { getNewMilestonesFromGrant } from './utils/exploreUnknownNewMilestonesFromGrant';
import { getSurfaceForMissionIndex } from './utils/exploreUnknownAlternativePath';
import {
  grantUnlocksExploreUnknownLevel100Door,
  markExploreUnknownLevel100DoorUnlockSeen,
} from './utils/exploreUnknownLevel100DoorUnlock';
import ExploreUnknownDarknessMeter from './components/ExploreUnknownDarknessMeter';
import AchievementUnlockedToast from './notifications/AchievementUnlockedToast';
import { usePendingAchievementToasts } from './hooks/usePendingAchievementToasts';
import { requestPendingSeasonEndCheck } from './utils/requestPendingSeasonEndCheck';
import AfterBattleButton from './components/AfterBattleButton';
import {
  getFactionBattleEndPresentation,
  isAfterBattleFactionPresentationEligible,
} from './config/factionBattleEndPresentation';
import { resolvePlayerFactionFromBattleContext } from './utils/resolvePlayerFactionFromBattleContext';
import {
  addDismissedWarEffortMissionId,
  hasWarEffortMissionOverMessageBeenShown,
  markWarEffortMissionOverMessageShown,
} from './utils/warEffortDismissedMissions';
const factionColors = {
  'Remnants of Humanity': '#556B2F',
  'Exolot Dominion': '#4B0082',
  'League of Shadows': '#1A1A1A',
  'Ra\'s Children': '#F4A460',
  'Asrah': '#8B0000',
  'The Sentients': '#ADD8E6',
  'Combined': '#FFA500'
};


function AfterBattle() {
  const location = useLocation();
  const navigate = useNavigate();
  const { current: pendingToast, dismissCurrent: dismissPendingToast } = usePendingAchievementToasts();
  const { t, i18n: i18nInstance } = useTranslation();
  const goToCampaignTutorialHub = () => {
    navigate('/campaign', { state: { selectedTopTab: 'tutorial' } });
  };
  const goToExploreUnknown = () => {
    playSoundEffect('/sounds/click.mp3');
    navigate('/explore-unknown');
  };
  
  // Viewport scaling for responsive design
  const { 
    scale: viewportScale, 
    offsetX: viewportOffsetX, 
    offsetY: viewportOffsetY,
    baseWidth: containerBaseWidth,
    baseHeight: containerBaseHeight,
    isMobile: isContainerMobile
  } = useContainerScale();

  useEffect(() => {
    if (!isContainerMobile) {
      return undefined;
    }
    document.documentElement.classList.add('afterbattle-mobile');
    return () => {
      document.documentElement.classList.remove('afterbattle-mobile');
    };
  }, [isContainerMobile]);
  
  const resultFromState = location.state?.result; // 'win' | 'loss'
  const params = new URLSearchParams(location.search);
  const resultFromQuery = params.get('result');
  const result = resultFromState || resultFromQuery; // 'win' | 'loss'
  const opponentStrategyId = location.state?.opponentStrategyId;
  const gameId = location.state?.gameId;
  const isFriendlyMatch = location.state?.isFriendlyMatch || false;
  const isTournamentMatch = location.state?.isTournamentMatch || location.state?.gameState?.isTournamentMatch || false;
  const gameState = location.state?.gameState;
  const tournamentId = gameState?.tournamentId;
  const tournamentMatchId = gameState?.tournamentMatchId;

  const [opponentStrategy, setOpponentStrategy] = useState(null);
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [rewards, setRewards] = useState([]);
  const [rewardsLoading, setRewardsLoading] = useState(false);
  const [rewardsGranted, setRewardsGranted] = useState(false);
  const [rewardsError, setRewardsError] = useState(null); // ✅ FIX: Track reward loading errors
  const [dailyLimitReached, setDailyLimitReached] = useState(false); // ✅ DAILY BOT GAME REWARD LIMIT: Track if daily limit reached
  const [revealedRewards, setRevealedRewards] = useState([false, false, false]);
  const [enrichedRewards, setEnrichedRewards] = useState([null, null, null]); // Store enriched order data
  const videoRefs = React.useRef([null, null, null]);

  const isWarEffort = location.state?.isWarEffort || !!(location.state?.gameState?.warEffortMissionId);
  const fromWarEffortMissionOver = location.state?.fromWarEffortMissionOver === true;
  const isWarEffortHQEnding = isWarEffort && (
    fromWarEffortMissionOver
    || (location.state?.gameState?.warEffortBattleType === 'enemy_hq' && result === 'win')
  );
  // Mission-over redirect sets missionIdForDismiss; HQ conquer from BattleUI only has warEffortMissionId on gameState.
  // Both must dismiss on Continue or WareEffort will reopen AfterBattle and replay rewards.
  const missionIdForDismiss =
    location.state?.missionIdForDismiss
    ?? location.state?.gameState?.warEffortMissionId
    ?? null;
  const isCampaign = location.state?.isCampaign || !!(location.state?.gameState?.campaignMode && (location.state?.gameState?.missionId || location.state?.missionId));
  const missionId = location.state?.missionId || gameState?.missionId || null;
  const isTutorialMission4Complete = missionId === 'chapter_1_mission_4';
  const strategyRewardWellDoneKey = isTutorialMission4Complete
    ? 'afterBattle.wellDoneSummaryMission4'
    : 'afterBattle.wellDoneSummaryMission2And4';
  const renderStrategyRewardCompletionActions = () => (
    <div className="afterbattle-completion-actions">
      {missionId === 'chapter_1_mission_2' && (
        <AfterBattleButton
          variant="golden"
          onClick={() => navigate('/campaign-battle/chapter_1_mission_3')}
        >
          {t('afterBattle.nextTutorialMission')}
        </AfterBattleButton>
      )}
      {isTutorialMission4Complete && (
        <AfterBattleButton
          variant="golden"
          onClick={goToExploreUnknown}
        >
          {t('afterBattle.exploreUnknownRecommended')}
        </AfterBattleButton>
      )}
      <AfterBattleButton
        variant="blue"
        onClick={() => goToCampaignTutorialHub()}
      >
        {t('afterBattle.backToTutorial')}
      </AfterBattleButton>
    </div>
  );
  const isCampaignTutorial = isCampaign && (missionId === 'chapter_1_mission_1' || missionId === 'chapter_1_mission_2' || missionId === 'chapter_1_mission_3');
  const isAutobattler = location.state?.isAutobattler || location.state?.gameState?.isAutobattler || false;
  const playerBotWon = isAutobattler && location.state?.gameState?.winner === location.state?.gameState?.playerBotId;

  const isEngageEnemyQueue = location.state?.isEngageEnemyQueue === true;
  const rematch = useRematchOptional();
  const isRematchGame =
    gameState?.isRematch === true ||
    gameState?.isMirrorRematch === true ||
    location.state?.gameState?.isRematch === true ||
    location.state?.gameState?.isMirrorRematch === true;
  const showPvpRematchButtons =
    isEngageEnemyQueue &&
    !isFriendlyMatch &&
    !isTournamentMatch &&
    !isRematchGame &&
    rematch &&
    !rematch.incomingOffer &&
    Boolean(gameId);
  const waitingForRematchOnThisGame =
    showPvpRematchButtons &&
    rematch.inRematchWait &&
    rematch.outgoingWait?.gameId === gameId;
  const isExploreUnknownRun =
    !!(location.state?.exploreUnknownRun || location.state?.gameState?.exploreUnknownMissionIndex != null);
  const isExploreUnknownHomecoming =
    isExploreUnknownRun && location.state?.exploreUnknownRunHomecoming === true;
  const isExploreUnknownLoss = isExploreUnknownRun && result === 'loss';
  const isExploreUnknownDarknessLoss =
    isExploreUnknownLoss && location.state?.gameState?.exploreUnknownDarknessLoss === true;
  const isTrainPvE =
    location.state?.trainPvE === true || isTrainPvEBattle(location.state?.gameState);
  const isEliteForceJoinedCountry = isEliteForceJoinedCountryBattle(location.state?.gameState);
  const [pvpLadderData, setPvpLadderData] = useState(() => location.state?.pvpLadder ?? null);
  const [engageEnemyRankPhase, setEngageEnemyRankPhase] = useState(() => {
    if (!location.state?.isEngageEnemyQueue) return 'rewards';
    if (location.state?.devPvpRankMock && location.state?.pvpLadder) return 'rank';
    return 'pending';
  });

  // War Effort HQ ending: show "Mission successful" / "Mission failed" first, then transition to rewards
  const [warEffortMessagePhase, setWarEffortMessagePhase] = useState(() => (
    isWarEffortHQEnding && gameId && hasWarEffortMissionOverMessageBeenShown(gameId)
      ? 'rewards'
      : 'message'
  )); // 'message' | 'rewards'
  
  const [tutorialRewardChoice, setTutorialRewardChoice] = useState(null);
  const [tutorialRewardGranted, setTutorialRewardGranted] = useState(null);
  const [tutorialRewardLoading, setTutorialRewardLoading] = useState(false);
  const [tutorialChoiceOrders, setTutorialChoiceOrders] = useState(null); // id -> full order for OrderDesign

  // Mission 2 strategy reward
  const [m2StrategyChoices, setM2StrategyChoices] = useState(null); // [{ key, value }] or []
  const [m2StrategyLoading, setM2StrategyLoading] = useState(false);
  const [m2StrategyGranted, setM2StrategyGranted] = useState(null); // { strategyName, grantedOrders }
  const [neutralCountryChoice, setNeutralCountryChoice] = useState(null); // { options: [...], missionId, ordersToKeep }
  const [neutralCountryGranted, setNeutralCountryGranted] = useState(false);
  const [neutralCountryGrantedOrders, setNeutralCountryGrantedOrders] = useState([]); // [{ id, name, rarity }] for confirmation
  const [neutralCountryGrantedBuilding, setNeutralCountryGrantedBuilding] = useState(null); // { type: 'building', data: { building_id } } | null
  const [neutralCountryLoading, setNeutralCountryLoading] = useState(false);
  const [neutralCountryChoiceOrders, setNeutralCountryChoiceOrders] = useState(null); // id -> full order
  const [neutralCountrySelectedIds, setNeutralCountrySelectedIds] = useState([]);
  const [euRunRewardsSummary, setEuRunRewardsSummary] = useState(null);
  const [euRunRewardsLoading, setEuRunRewardsLoading] = useState(false);
  const [euRunRewardsError, setEuRunRewardsError] = useState(null);
  const exploreUnknownXpGrant =
    location.state?.exploreUnknownXpGrant ??
    location.state?.gameState?.exploreUnknownXpGrant ??
    null;
  const legendaryOrderUnlockReward = exploreUnknownXpGrant?.legendaryOrderUnlockReward ?? null;
  const exploreUnknownBonusOrderQueue = useMemo(() => {
    const queue = [];
    if (legendaryOrderUnlockReward) queue.push(legendaryOrderUnlockReward);
    const bonusOrderRewards = exploreUnknownXpGrant?.bonusOrderRewards;
    if (Array.isArray(bonusOrderRewards)) {
      queue.push(...bonusOrderRewards);
    }
    return queue;
  }, [exploreUnknownXpGrant, legendaryOrderUnlockReward]);
  const hasExploreUnknownBonusOrderQueue = exploreUnknownBonusOrderQueue.length > 0;
  const hasRewriteFragmentMapProcGrant = exploreUnknownXpGrant?.rewriteFragmentMapProcGranted === true;
  const hasEpFragmentMapProcGrant = exploreUnknownXpGrant?.epFragmentMapProcGranted === true;
  const exploreUnknownRefinedErrayBossDrop = useMemo(() => {
    const raw =
      location.state?.exploreUnknownRefinedErrayBossDrop ??
      location.state?.gameState?.exploreUnknownRefinedErrayBossDrop ??
      null;
    if (raw == null || typeof raw !== 'object') {
      return null;
    }
    const missionIndex = Number(raw.missionIndex);
    if (!Number.isInteger(missionIndex) || missionIndex <= 0) {
      return null;
    }
    return { missionIndex };
  }, [location.state]);
  const hasRefinedErrayBossDropGrant = exploreUnknownRefinedErrayBossDrop != null;
  const exploreUnknownNewMilestones = useMemo(
    () => getNewMilestonesFromGrant(exploreUnknownXpGrant),
    [exploreUnknownXpGrant]
  );
  const hasExploreUnknownMilestoneUnlocks = exploreUnknownNewMilestones.length > 0;
  const unlocksLevel100DoorOnVictory = useMemo(
    () =>
      isExploreUnknownRun &&
      result === 'win' &&
      grantUnlocksExploreUnknownLevel100Door(exploreUnknownXpGrant),
    [isExploreUnknownRun, result, exploreUnknownXpGrant]
  );
  const exploreUnknownAtMaxLevel =
    exploreUnknownXpGrant != null &&
    Number(exploreUnknownXpGrant.levelAfter) >= 100;
  const showExploreUnknownXpOverlay =
    isExploreUnknownRun &&
    result === 'win' &&
    exploreUnknownXpGrant != null &&
    !exploreUnknownAtMaxLevel;
  const [exploreUnknownXpAnimationDone, setExploreUnknownXpAnimationDone] = useState(
    () => !showExploreUnknownXpOverlay
  );
  const [milestoneUnlockIndex, setMilestoneUnlockIndex] = useState(0);
  const [showMilestoneUnlockOverlay, setShowMilestoneUnlockOverlay] = useState(false);
  const [showLevel100DoorUnlockOverlay, setShowLevel100DoorUnlockOverlay] = useState(false);
  const [exploreUnknownMilestoneFlowDone, setExploreUnknownMilestoneFlowDone] = useState(
    () => !hasExploreUnknownMilestoneUnlocks && !unlocksLevel100DoorOnVictory
  );
  const [level100DoorUnlockFlowDone, setLevel100DoorUnlockFlowDone] = useState(
    () => !unlocksLevel100DoorOnVictory
  );
  const [bonusOrderQueueIndex, setBonusOrderQueueIndex] = useState(0);
  const [showBonusOrderRewardModal, setShowBonusOrderRewardModal] = useState(false);
  const [bonusOrderRewardFlowDone, setBonusOrderRewardFlowDone] = useState(
    () => !hasExploreUnknownBonusOrderQueue
  );
  const [showRewriteFragmentMapProcModal, setShowRewriteFragmentMapProcModal] = useState(false);
  const [rewriteFragmentMapProcFlowDone, setRewriteFragmentMapProcFlowDone] = useState(
    () => !hasRewriteFragmentMapProcGrant
  );
  const [showEpFragmentMapProcModal, setShowEpFragmentMapProcModal] = useState(false);
  const [epFragmentMapProcFlowDone, setEpFragmentMapProcFlowDone] = useState(
    () => !hasEpFragmentMapProcGrant
  );
  const [showRefinedErrayBossDropModal, setShowRefinedErrayBossDropModal] = useState(false);
  const [refinedErrayBossDropFlowDone, setRefinedErrayBossDropFlowDone] = useState(
    () => !hasRefinedErrayBossDropGrant
  );
  const activeBonusOrderReward = showBonusOrderRewardModal
    ? exploreUnknownBonusOrderQueue[bonusOrderQueueIndex] ?? null
    : null;
  const exploreUnknownVictoryFlowDone =
    exploreUnknownXpAnimationDone &&
    exploreUnknownMilestoneFlowDone &&
    level100DoorUnlockFlowDone &&
    bonusOrderRewardFlowDone &&
    rewriteFragmentMapProcFlowDone &&
    epFragmentMapProcFlowDone &&
    refinedErrayBossDropFlowDone;

  const openRefinedErrayBossDropModalIfNeeded = useCallback(() => {
    if (hasRefinedErrayBossDropGrant && !refinedErrayBossDropFlowDone) {
      setShowRefinedErrayBossDropModal(true);
      return true;
    }
    return false;
  }, [hasRefinedErrayBossDropGrant, refinedErrayBossDropFlowDone]);

  const openBonusOrderRewardModalIfNeeded = useCallback(() => {
    if (hasExploreUnknownBonusOrderQueue) {
      setBonusOrderQueueIndex(0);
      setShowBonusOrderRewardModal(true);
      return true;
    }
    return false;
  }, [hasExploreUnknownBonusOrderQueue]);

  const openPostRewriteFragmentVictoryStep = useCallback(() => {
    if (hasExploreUnknownMilestoneUnlocks) {
      setMilestoneUnlockIndex(0);
      setShowMilestoneUnlockOverlay(true);
      return;
    }
    if (openBonusOrderRewardModalIfNeeded()) {
      return;
    }
    openRefinedErrayBossDropModalIfNeeded();
  }, [
    hasExploreUnknownMilestoneUnlocks,
    openBonusOrderRewardModalIfNeeded,
    openRefinedErrayBossDropModalIfNeeded,
  ]);

  const openPostMapProcVictoryStep = useCallback(() => {
    if (hasEpFragmentMapProcGrant && !epFragmentMapProcFlowDone) {
      setShowEpFragmentMapProcModal(true);
      return;
    }
    openPostRewriteFragmentVictoryStep();
  }, [
    hasEpFragmentMapProcGrant,
    epFragmentMapProcFlowDone,
    openPostRewriteFragmentVictoryStep,
  ]);

  const handleBonusOrderRewardDismiss = useCallback(() => {
    const next = bonusOrderQueueIndex + 1;
    if (next < exploreUnknownBonusOrderQueue.length) {
      setBonusOrderQueueIndex(next);
      return;
    }
    setShowBonusOrderRewardModal(false);
    setBonusOrderRewardFlowDone(true);
    if (openRefinedErrayBossDropModalIfNeeded()) {
      return;
    }
    setExploreUnknownMilestoneFlowDone(true);
  }, [
    bonusOrderQueueIndex,
    exploreUnknownBonusOrderQueue.length,
    openRefinedErrayBossDropModalIfNeeded,
  ]);

  const handleRefinedErrayBossDropDismiss = useCallback(() => {
    setShowRefinedErrayBossDropModal(false);
    setRefinedErrayBossDropFlowDone(true);
    setExploreUnknownMilestoneFlowDone(true);
  }, []);

  const openLegendaryOrderUnlockModalIfNeeded = openBonusOrderRewardModalIfNeeded;

  const finishExploreUnknownMilestoneVictoryStep = useCallback(() => {
    if (openLegendaryOrderUnlockModalIfNeeded()) {
      return;
    }
    if (openRefinedErrayBossDropModalIfNeeded()) {
      return;
    }
    setExploreUnknownMilestoneFlowDone(true);
  }, [openLegendaryOrderUnlockModalIfNeeded, openRefinedErrayBossDropModalIfNeeded]);

  const handleExploreUnknownXpAnimationComplete = useCallback(() => {
    setExploreUnknownXpAnimationDone(true);
    if (hasRewriteFragmentMapProcGrant) {
      setShowRewriteFragmentMapProcModal(true);
      return;
    }
    if (hasEpFragmentMapProcGrant) {
      setShowEpFragmentMapProcModal(true);
      return;
    }
    openPostRewriteFragmentVictoryStep();
  }, [
    hasRewriteFragmentMapProcGrant,
    hasEpFragmentMapProcGrant,
    openPostRewriteFragmentVictoryStep,
  ]);

  const handleRewriteFragmentMapProcDismiss = useCallback(() => {
    setShowRewriteFragmentMapProcModal(false);
    setRewriteFragmentMapProcFlowDone(true);
    openPostMapProcVictoryStep();
  }, [openPostMapProcVictoryStep]);

  const handleEpFragmentMapProcDismiss = useCallback(() => {
    setShowEpFragmentMapProcModal(false);
    setEpFragmentMapProcFlowDone(true);
    openPostRewriteFragmentVictoryStep();
  }, [openPostRewriteFragmentVictoryStep]);

  const openLevel100DoorUnlockOverlayIfNeeded = useCallback(() => {
    if (!unlocksLevel100DoorOnVictory || level100DoorUnlockFlowDone) {
      return false;
    }
    setShowLevel100DoorUnlockOverlay(true);
    return true;
  }, [unlocksLevel100DoorOnVictory, level100DoorUnlockFlowDone]);

  const handleLevel100DoorUnlockComplete = useCallback(() => {
    setShowLevel100DoorUnlockOverlay(false);
    markExploreUnknownLevel100DoorUnlockSeen();
    setLevel100DoorUnlockFlowDone(true);
    finishExploreUnknownMilestoneVictoryStep();
  }, [finishExploreUnknownMilestoneVictoryStep]);

  const handleMilestoneUnlockComplete = useCallback(() => {
    const completedLevel = exploreUnknownNewMilestones[milestoneUnlockIndex];
    const next = milestoneUnlockIndex + 1;
    if (completedLevel === 100 && unlocksLevel100DoorOnVictory) {
      setShowMilestoneUnlockOverlay(false);
      setShowLevel100DoorUnlockOverlay(true);
      return;
    }
    if (next < exploreUnknownNewMilestones.length) {
      setMilestoneUnlockIndex(next);
      return;
    }
    setShowMilestoneUnlockOverlay(false);
    if (openLevel100DoorUnlockOverlayIfNeeded()) {
      return;
    }
    finishExploreUnknownMilestoneVictoryStep();
  }, [
    milestoneUnlockIndex,
    exploreUnknownNewMilestones,
    unlocksLevel100DoorOnVictory,
    openLevel100DoorUnlockOverlayIfNeeded,
    finishExploreUnknownMilestoneVictoryStep,
  ]);

  const handleLegendaryOrderUnlockDismiss = handleBonusOrderRewardDismiss;

  useEffect(() => {
    setMilestoneUnlockIndex(0);
    setShowMilestoneUnlockOverlay(false);
    setShowLevel100DoorUnlockOverlay(false);
    setExploreUnknownMilestoneFlowDone(
      !hasExploreUnknownMilestoneUnlocks && !unlocksLevel100DoorOnVictory
    );
    setLevel100DoorUnlockFlowDone(!unlocksLevel100DoorOnVictory);
    setShowBonusOrderRewardModal(false);
    setBonusOrderQueueIndex(0);
    setBonusOrderRewardFlowDone(!hasExploreUnknownBonusOrderQueue);
    setShowRewriteFragmentMapProcModal(false);
    setRewriteFragmentMapProcFlowDone(!hasRewriteFragmentMapProcGrant);
    setShowEpFragmentMapProcModal(false);
    setEpFragmentMapProcFlowDone(!hasEpFragmentMapProcGrant);
    setShowRefinedErrayBossDropModal(false);
    setRefinedErrayBossDropFlowDone(!hasRefinedErrayBossDropGrant);
    setExploreUnknownXpAnimationDone(!showExploreUnknownXpOverlay);
  }, [
    exploreUnknownXpGrant,
    hasExploreUnknownMilestoneUnlocks,
    hasExploreUnknownBonusOrderQueue,
    hasRewriteFragmentMapProcGrant,
    hasEpFragmentMapProcGrant,
    hasRefinedErrayBossDropGrant,
    showExploreUnknownXpOverlay,
    unlocksLevel100DoorOnVictory,
  ]);

  useEffect(() => {
    if (
      !isExploreUnknownRun ||
      result !== 'win' ||
      showExploreUnknownXpOverlay ||
      hasExploreUnknownMilestoneUnlocks ||
      showLevel100DoorUnlockOverlay ||
      !unlocksLevel100DoorOnVictory ||
      level100DoorUnlockFlowDone
    ) {
      return;
    }
    setExploreUnknownXpAnimationDone(true);
    setShowLevel100DoorUnlockOverlay(true);
  }, [
    isExploreUnknownRun,
    result,
    showExploreUnknownXpOverlay,
    hasExploreUnknownMilestoneUnlocks,
    showLevel100DoorUnlockOverlay,
    unlocksLevel100DoorOnVictory,
    level100DoorUnlockFlowDone,
  ]);

  useEffect(() => {
    if (
      !isExploreUnknownRun ||
      result !== 'win' ||
      showExploreUnknownXpOverlay ||
      !hasRewriteFragmentMapProcGrant ||
      rewriteFragmentMapProcFlowDone
    ) {
      return;
    }
    setExploreUnknownXpAnimationDone(true);
    setShowRewriteFragmentMapProcModal(true);
  }, [
    isExploreUnknownRun,
    result,
    showExploreUnknownXpOverlay,
    hasRewriteFragmentMapProcGrant,
    rewriteFragmentMapProcFlowDone,
  ]);

  useEffect(() => {
    if (
      !isExploreUnknownRun ||
      result !== 'win' ||
      showExploreUnknownXpOverlay ||
      hasRewriteFragmentMapProcGrant ||
      !hasEpFragmentMapProcGrant ||
      epFragmentMapProcFlowDone
    ) {
      return;
    }
    setExploreUnknownXpAnimationDone(true);
    setShowEpFragmentMapProcModal(true);
  }, [
    isExploreUnknownRun,
    result,
    showExploreUnknownXpOverlay,
    hasRewriteFragmentMapProcGrant,
    hasEpFragmentMapProcGrant,
    epFragmentMapProcFlowDone,
  ]);

  useEffect(() => {
    if (
      !isExploreUnknownRun ||
      result !== 'win' ||
      showExploreUnknownXpOverlay ||
      (hasRewriteFragmentMapProcGrant && !rewriteFragmentMapProcFlowDone) ||
      (hasEpFragmentMapProcGrant && !epFragmentMapProcFlowDone) ||
      !      hasExploreUnknownMilestoneUnlocks ||
      exploreUnknownMilestoneFlowDone ||
      showLevel100DoorUnlockOverlay ||
      (unlocksLevel100DoorOnVictory && !level100DoorUnlockFlowDone)
    ) {
      return;
    }
    setExploreUnknownXpAnimationDone(true);
    setMilestoneUnlockIndex(0);
    setShowMilestoneUnlockOverlay(true);
  }, [
    isExploreUnknownRun,
    result,
    showExploreUnknownXpOverlay,
    hasRewriteFragmentMapProcGrant,
    rewriteFragmentMapProcFlowDone,
    hasEpFragmentMapProcGrant,
    epFragmentMapProcFlowDone,
    hasExploreUnknownMilestoneUnlocks,
    exploreUnknownMilestoneFlowDone,
    showLevel100DoorUnlockOverlay,
    unlocksLevel100DoorOnVictory,
    level100DoorUnlockFlowDone,
  ]);

  useEffect(() => {
    if (
      !isExploreUnknownRun ||
      result !== 'win' ||
      showExploreUnknownXpOverlay ||
      (hasRewriteFragmentMapProcGrant && !rewriteFragmentMapProcFlowDone) ||
      (hasEpFragmentMapProcGrant && !epFragmentMapProcFlowDone) ||
      hasExploreUnknownMilestoneUnlocks ||
      !hasExploreUnknownBonusOrderQueue ||
      bonusOrderRewardFlowDone
    ) {
      return;
    }
    setExploreUnknownXpAnimationDone(true);
    setBonusOrderQueueIndex(0);
    setShowBonusOrderRewardModal(true);
  }, [
    isExploreUnknownRun,
    result,
    showExploreUnknownXpOverlay,
    hasExploreUnknownMilestoneUnlocks,
    hasExploreUnknownBonusOrderQueue,
    bonusOrderRewardFlowDone,
  ]);

  useEffect(() => {
    if (
      !isExploreUnknownRun ||
      result !== 'win' ||
      showExploreUnknownXpOverlay ||
      (hasRewriteFragmentMapProcGrant && !rewriteFragmentMapProcFlowDone) ||
      (hasEpFragmentMapProcGrant && !epFragmentMapProcFlowDone) ||
      hasExploreUnknownMilestoneUnlocks ||
      hasExploreUnknownBonusOrderQueue ||
      !hasRefinedErrayBossDropGrant ||
      refinedErrayBossDropFlowDone ||
      showRefinedErrayBossDropModal
    ) {
      return;
    }
    setExploreUnknownXpAnimationDone(true);
    setShowRefinedErrayBossDropModal(true);
  }, [
    isExploreUnknownRun,
    result,
    showExploreUnknownXpOverlay,
    hasRewriteFragmentMapProcGrant,
    rewriteFragmentMapProcFlowDone,
    hasEpFragmentMapProcGrant,
    epFragmentMapProcFlowDone,
    hasExploreUnknownMilestoneUnlocks,
    hasExploreUnknownBonusOrderQueue,
    hasRefinedErrayBossDropGrant,
    refinedErrayBossDropFlowDone,
    showRefinedErrayBossDropModal,
  ]);
  
  const isKendrexSilentAfterBattleMission = isExploreUnknownKendrexColonySilentAfterBattleMissionIndex(
    location.state?.gameState?.exploreUnknownMissionIndex
  );

  const factionBattleEndPresentation = useMemo(() => {
    if (result !== 'win' && result !== 'loss') {
      return null;
    }
    if (
      !isAfterBattleFactionPresentationEligible({
        isWarEffortHQEnding,
        isExploreUnknownHomecoming,
        isExploreUnknownLoss,
        hasBattleFactionContext: !!(location.state?.playerFactionName || gameState?.state),
      })
    ) {
      return null;
    }
    const userId = localStorage.getItem('userId');
    const playerFactionName = resolvePlayerFactionFromBattleContext({
      playerFactionName: location.state?.playerFactionName,
      gameState,
      userId,
    });
    return getFactionBattleEndPresentation(playerFactionName, result, {
      assetSlug: location.state?.battleEndAssetSlug,
      componentFaction: location.state?.battleEndComponentFaction,
    });
  }, [
    result,
    gameState,
    location.state?.playerFactionName,
    location.state?.battleEndAssetSlug,
    location.state?.battleEndComponentFaction,
    isWarEffortHQEnding,
    isExploreUnknownHomecoming,
    isExploreUnknownLoss,
  ]);

  useEffect(() => {
    setExploreUnknownKendrexColonyBattleAudioSuppressed(isKendrexSilentAfterBattleMission);
    return () => setExploreUnknownKendrexColonyBattleAudioSuppressed(false);
  }, [isKendrexSilentAfterBattleMission]);

  // Play appropriate music: War Effort HQ ending uses military/somber; faction battles use faction tracks
  const musicTrack = isKendrexSilentAfterBattleMission
    ? null
    : isWarEffortHQEnding
      ? (result === 'win' ? '/music/military_2.mp3' : result === 'loss' ? '/music/somber_1.mp3' : null)
      : factionBattleEndPresentation
        ? factionBattleEndPresentation.musicSrc
        : (result === 'win' ? '/music/Victory_1.mp3' : result === 'loss' ? '/music/loss_1.mp3' : null);
  const afterBattleMusicLoops = !(result === 'win' || result === 'loss');
  const afterBattleMusicFadeMs = afterBattleMusicLoops ? 2000 : 400;
  useMusic(musicTrack, 0, afterBattleMusicFadeMs, afterBattleMusicLoops);

  // War Effort HQ ending: after delay, hide big message and show rewards
  useEffect(() => {
    if (!isWarEffortHQEnding || warEffortMessagePhase !== 'message') return;
    const timer = setTimeout(() => {
      if (gameId) {
        markWarEffortMissionOverMessageShown(gameId);
      }
      setWarEffortMessagePhase('rewards');
    }, 2500);
    return () => clearTimeout(timer);
  }, [isWarEffortHQEnding, warEffortMessagePhase, gameId]);
  
  // Ensure videos play when rewards are loaded
  useEffect(() => {
    if (rewards.length > 0) {
      videoRefs.current.forEach((video, index) => {
        if (video && !revealedRewards[index]) {
          // Try to play the video, with a small delay to ensure it's ready
          const tryPlay = () => {
            if (video.readyState >= 2) { // HAVE_CURRENT_DATA or higher
              video.play().catch(err => {
                console.error(`Error playing video ${index}:`, err);
              });
            } else {
              // Wait for video to be ready
              video.addEventListener('loadeddata', () => {
                video.play().catch(err => {
                  console.error(`Error playing video ${index}:`, err);
                });
              }, { once: true });
            }
          };
          
          // Small delay to ensure DOM is ready
          setTimeout(tryPlay, 100);
        }
      });
    }
  }, [rewards, revealedRewards]);

  // Mission 2 & 4: fetch available strategy choices on win
  const isStrategyRewardMission = missionId === 'chapter_1_mission_2' || missionId === 'chapter_1_mission_4';
  useEffect(() => {
    if (!isCampaign || !isStrategyRewardMission || result !== 'win') return;

    // Dev override: use choices passed via location state
    if (location.state?.devM2Choices) {
      console.log('🧪 Dev: Using mock mission 2 strategy choices');
      setM2StrategyChoices(location.state.devM2Choices);
      return;
    }

    const token = localStorage.getItem('token');
    if (!token || !missionId) {
      console.error('Mission strategy reward: missing token or missionId', { missionId, isCampaign, result });
      setM2StrategyChoices([]);
      return;
    }
    fetch(`${API_URL}/campaign/mission2-strategy-choices?missionId=${encodeURIComponent(missionId)}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(async (r) => {
        const data = await r.json();
        if (!r.ok) {
          throw new Error(data.error || `Strategy choices request failed (${r.status})`);
        }
        return data;
      })
      .then(data => {
        console.log('🎁 Mission strategy choices:', data);
        setM2StrategyChoices(data.choices || []);
      })
      .catch(err => {
        console.error('Failed to fetch mission 2 strategy choices:', err);
        setM2StrategyChoices([]);
      });
  }, [isCampaign, isStrategyRewardMission, missionId, result, location.state]);

  const handleM2StrategySelect = async (preference) => {
    if (m2StrategyLoading) return;
    setM2StrategyLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/campaign/mission2-strategy-reward`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ preference, missionId })
      });
      if (res.ok) {
        const data = await res.json();
        setM2StrategyGranted(data);
      } else {
        const err = await res.json().catch(() => ({}));
        console.error('Mission 2 strategy reward failed:', err);
        alert(err.error || 'Failed to claim strategy reward');
      }
    } catch (err) {
      console.error('Mission 2 strategy reward error:', err);
      alert('Failed to claim strategy reward');
    } finally {
      setM2StrategyLoading(false);
    }
  };

  // Fetch full order data for tutorial reward choice options (Reckless Major 299, Unity Shard 322).
  // Use /order/:id/full because /all-orders excludes weapons – Unity Shard is a weapon, so it would be missing.
  useEffect(() => {
    if (!tutorialRewardChoice?.options?.length) {
      setTutorialChoiceOrders(null);
      return;
    }
    const token = localStorage.getItem('token');
    const langQuery = buildOrderDetailLangQuery(i18nInstance.language);
    const ids = tutorialRewardChoice.options.map(opt => opt.id);
    Promise.all(
      ids.map(id =>
        fetch(`${API_URL}/order/${id}/full?${langQuery}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
          .then(res => (res.ok ? res.json() : null))
          .then(order => (order && order.id != null ? [Number(order.id), order] : null))
      )
    )
      .then(results => {
        const map = {};
        results.forEach(r => {
          if (r) {
            const [id, order] = r;
            if (order.type_original) order.type = order.type_original;
            if (order.faction_name && !order.faction) order.faction = order.faction_name;
            map[id] = order;
          }
        });
        setTutorialChoiceOrders(map);
      })
      .catch(err => {
        console.error('Failed to fetch tutorial choice order data:', err);
        setTutorialChoiceOrders({});
      });
  }, [tutorialRewardChoice?.options, i18nInstance.language]);

  // Fetch full order data for neutral country conquest choice (3 orders to choose 1)
  useEffect(() => {
    if (!neutralCountryChoice?.options?.length) {
      setNeutralCountryChoiceOrders(null);
      return;
    }
    const token = localStorage.getItem('token');
    const langQuery = buildOrderDetailLangQuery(i18nInstance.language);
    const ids = neutralCountryChoice.options.map(opt => opt.id);
    Promise.all(
      ids.map(id =>
        fetch(`${API_URL}/order/${id}/full?${langQuery}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        })
          .then(res => (res.ok ? res.json() : null))
          .then(order => (order && order.id != null ? [Number(order.id), order] : null))
      )
    )
      .then(results => {
        const map = {};
        results.forEach(r => {
          if (r) {
            const [id, order] = r;
            if (order.type_original) order.type = order.type_original;
            if (order.faction_name && !order.faction) order.faction = order.faction_name;
            map[id] = order;
          }
        });
        setNeutralCountryChoiceOrders(map);
      })
      .catch(() => setNeutralCountryChoiceOrders(null));
  }, [neutralCountryChoice?.options, i18nInstance.language]);

  // Fetch battle rewards if gameId is available (skip for friendly matches, tournament matches, and autobattler losses)
  useEffect(() => {
    console.log('🎁 AfterBattle: Checking for rewards - gameId:', gameId, 'result:', result, 'rewardsGranted:', rewardsGranted, 'isFriendlyMatch:', isFriendlyMatch, 'isTournamentMatch:', isTournamentMatch, 'isAutobattler:', isAutobattler, 'playerBotWon:', playerBotWon, 'location.state:', location.state);
    
    // ✅ DEV: Check if rewards are passed via state (for dev demo)
    if (
      location.state?.devExploreUnknownWinMock === true ||
      (location.state?.rewards && Array.isArray(location.state.rewards))
    ) {
      if (location.state?.devExploreUnknownWinMock === true) {
        console.log('🎁 AfterBattle: Using Explore Unknown dev win mock from state');
      } else {
        console.log('🎁 AfterBattle: Using rewards from state (dev demo)');
      }
      const stateRewards = Array.isArray(location.state.rewards) ? location.state.rewards : [];
      const processedRewards = [];
      for (const reward of stateRewards) {
        processedRewards.push(reward);
        if (reward.type === 'order' && reward.data && reward.data.id) {
          const rewardIndex = processedRewards.length - 1;
          setEnrichedRewards(prev => {
            const newState = [...prev];
            newState[rewardIndex] = reward.data;
            return newState;
          });
        }
      }
      setRewards(processedRewards);
      setRewardsLoading(false);
      setRewardsGranted(true);
      return;
    }

    if (location.state?.devPvpRankMock) {
      if (location.state?.pvpLadder) {
        setPvpLadderData(location.state.pvpLadder);
        setEngageEnemyRankPhase('rank');
      } else {
        setEngageEnemyRankPhase('rewards');
      }
      setRewardsLoading(false);
      setRewards(Array.isArray(location.state.rewards) ? location.state.rewards : []);
      setRewardsGranted(true);
      return;
    }

    const isExploreUnknownRunEndSummaryScreen =
      !!(location.state?.exploreUnknownRun || location.state?.gameState?.exploreUnknownMissionIndex != null) &&
      (location.state?.exploreUnknownRunHomecoming === true ||
        (result === 'loss' && location.state?.gameState?.exploreUnknownDarknessLoss === true));

    if (isExploreUnknownRunEndSummaryScreen) {
      if (
        (location.state?.devExploreUnknownRunLossMock ||
          location.state?.devExploreUnknownRunHomecomingMock) &&
        location.state?.exploreUnknownRunRewardsSummary
      ) {
        setEuRunRewardsSummary(location.state.exploreUnknownRunRewardsSummary);
        setEuRunRewardsLoading(false);
        setEuRunRewardsError(null);
        setRewardsLoading(false);
        setRewardsGranted(true);
        return;
      }
      const runId = location.state?.gameState?.exploreUnknownRunId;
      if (runId == null || runId === '') {
        setEuRunRewardsError('Explore Unknown run end screen missing exploreUnknownRunId on game state');
        setEuRunRewardsLoading(false);
        setRewardsLoading(false);
        return;
      }
      setEuRunRewardsLoading(true);
      setEuRunRewardsError(null);
      setRewardsLoading(false);
      const token = localStorage.getItem('token');
      const langQuery = buildOrderDetailLangQuery(i18nInstance.language);
      fetch(`${API_URL}/explore-unknown/run/${runId}/rewards-summary?${langQuery}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((res) => {
          if (!res.ok) {
            return res.json().then((err) => {
              throw new Error(err.error || `Failed to load run rewards (${res.status})`);
            });
          }
          return res.json();
        })
        .then((data) => {
          setEuRunRewardsSummary(data);
          setEuRunRewardsLoading(false);
          setRewardsGranted(true);
        })
        .catch((err) => {
          console.error('AfterBattle: Explore Unknown run rewards summary error:', err);
          setEuRunRewardsError(
            err instanceof Error ? err.message : 'Failed to load run rewards summary'
          );
          setEuRunRewardsLoading(false);
        });
      return;
    }
    
    // ✅ FIX: Skip rewards loading for friendly matches, tournament matches, autobattler losses, campaign losses, and campaign mission 2 or 4 (strategy-reward missions; only load strategy choices, not battle_rewards)
    if (isFriendlyMatch || isTournamentMatch || (isAutobattler && !playerBotWon) || (isCampaignTutorial && result === 'loss') || (isCampaign && (missionId === 'chapter_1_mission_2' || missionId === 'chapter_1_mission_4'))) {
      console.log('🎁 AfterBattle: Skipping rewards for friendly/tournament match, autobattler loss, campaign loss, or mission 2/4');
      setRewardsLoading(false);
      return;
    }
    // Train (menu PvE vs bot): no rewards — do not call /battle-rewards (Explore Unknown still loads normally)
    if (isTrainPvE) {
      console.log('🎁 AfterBattle: Skipping rewards fetch for Train PvE');
      setRewardsLoading(false);
      setRewardsGranted(true);
      return;
    }
    // Joined Elite Force country defense (owner plays themselves): no rewards defined
    if (isEliteForceJoinedCountry) {
      console.log('🎁 AfterBattle: Skipping rewards fetch for Elite Force joined country battle');
      setRewardsLoading(false);
      setRewardsGranted(true);
      return;
    }
    if (gameId && !rewardsGranted) {
      setRewardsLoading(true);
      const token = localStorage.getItem('token');
      
      // ✅ FIX: Retry logic for fetching rewards
      let retryCount = 0;
      const maxRetries = 5;
      const retryDelay = 2000; // 2 seconds
      
      const fetchRewards = async () => {
        try {
          console.log(`🎁 AfterBattle: Fetching rewards for gameId: ${gameId} (attempt ${retryCount + 1}/${maxRetries})`);
          const langQuery = buildOrderDetailLangQuery(i18nInstance.language);
          const res = await fetch(`${API_URL}/battle-rewards/${gameId}?${langQuery}`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
          
          console.log('🎁 AfterBattle: Rewards fetch response status:', res.status, 'for result:', result);
          
          // War Effort mission-over: 404 = no rewards row (e.g. leaver). Same as PVE resign — treat as final, do not retry.
          if (fromWarEffortMissionOver && res.status === 404) {
            console.log('🎁 AfterBattle: War Effort mission over, no rewards (e.g. left mission) — treating as final');
            setRewards([]);
            setRewardsGranted(true);
            setRewardsLoading(false);
            return;
          }
          
          // ✅ FIX: Handle 202 (retry needed) and 404 (not found yet)
          if (res.status === 202 || res.status === 404) {
            const data = await res.json();
            if (data.retry && retryCount < maxRetries) {
              console.log(`🎁 AfterBattle: Rewards not ready, retrying in ${retryDelay}ms... (${retryCount + 1}/${maxRetries})`);
              retryCount++;
              setTimeout(fetchRewards, retryDelay);
              return;
            } else {
              console.error('🎁 AfterBattle: Max retries reached or retry not requested');
              setRewardsError('Rewards are still being calculated. Please refresh the page in a moment.');
              setRewardsLoading(false);
              return;
            }
          }
          
          if (!res.ok) {
            const err = await res.json();
            console.error('🎁 AfterBattle: Rewards fetch error response:', err, 'status:', res.status);
            setRewardsLoading(false);
            return;
          }
          
          const data = await res.json();
          console.log('🎁 AfterBattle: Rewards data received:', data, 'for result:', result);

          setPvpLadderData(data.pvpLadder ?? null);
          if (isEngageEnemyQueue) {
            setEngageEnemyRankPhase(data.pvpLadder ? 'rank' : 'rewards');
          }
          
          // ✅ CAMPAIGN TUTORIAL: Check for reward choice (Reckless Major or Unity Shard)
          const tutorialChoice = data.rewards?.find(r => r?.type === 'tutorial_reward_choice');
          if (tutorialChoice) {
            setTutorialRewardChoice(tutorialChoice);
            setRewards([]);
            setRewardsLoading(false);
            return;
          }
          // ✅ NEUTRAL COUNTRY CONQUEST/SUBVERSION: 3 orders to choose 1 for attack strategy
          const neutralChoice = data.rewards?.find(r => r?.type === 'neutral_country_choice');
          const hasMissionContext = gameState?.warEffortMissionId || location.state?.missionId;
          if (neutralChoice && hasMissionContext) {
            setNeutralCountryChoice(neutralChoice);
            setRewards([]);
            setRewardsLoading(false);
            return;
          }
          // ✅ CAMPAIGN TUTORIAL: Empty rewards = second playthrough (no rewards)
          if (isCampaignTutorial && result === 'win' && (!data.rewards || data.rewards.length === 0)) {
            setRewards([]);
            setRewardsGranted(true);
            setRewardsLoading(false);
            return;
          }
          
          // ✅ FIX: Check if no rewards (e.g., resigned against bot or daily limit) - final state, don't retry
          if (data.noRewards === true) {
            console.log('🎁 AfterBattle: No rewards (e.g., resigned against bot or daily limit) - skipping rewards display');
            setRewards([]);
            setRewardsGranted(true);
            setRewardsLoading(false);
            // ✅ DAILY BOT GAME REWARD LIMIT: Check if daily limit reached
            if (data.dailyLimitReached === true) {
              setDailyLimitReached(true);
            }
            return;
          }
          
          // War Effort mission over (player was not present): backend already stored final state; empty rewards = e.g. loser — don't retry
          if (fromWarEffortMissionOver && (!data?.rewards || !Array.isArray(data.rewards) || data.rewards.length === 0)) {
            console.log('🎁 AfterBattle: War Effort mission over with no rewards (e.g. loss) — treating as final');
            setRewards([]);
            setRewardsGranted(true);
            setRewardsLoading(false);
            return;
          }

          // ✅ FIX: Validate rewards array
          if (!data || !data.rewards || !Array.isArray(data.rewards) || data.rewards.length === 0) {
            if (retryCount < maxRetries) {
              console.log(`🎁 AfterBattle: Empty rewards array, retrying in ${retryDelay}ms... (${retryCount + 1}/${maxRetries})`);
              retryCount++;
              setTimeout(fetchRewards, retryDelay);
              return;
            } else {
              console.error('🎁 AfterBattle: Max retries reached, rewards still empty');
              setRewardsError('Rewards are still being calculated. Please refresh the page in a moment.');
              setRewardsLoading(false);
              return;
            }
          }
          
          console.log('🎁 AfterBattle: Setting rewards:', data.rewards, 'outcome:', data.outcome, 'for result:', result);
          // Process rewards: display each reward individually (including Billy Bucks)
          const processedRewards = [];
          
          for (const reward of data.rewards) {
            processedRewards.push(reward);
            // ✅ FIX: Backend already enriches rewards with complete data, so use it directly
            // Find the index of this reward and set enrichedRewards immediately
            if (reward.type === 'order' && reward.data && reward.data.id) {
              const rewardIndex = processedRewards.length - 1;
              setEnrichedRewards(prev => {
                const newState = [...prev];
                newState[rewardIndex] = reward.data; // Use the enriched data from backend
                return newState;
              });
            }
          }
          
          // ✅ Flip layout for 3–5 rewards (face-down, click to reveal). Campaign grid (all orders, no flip) only for 6+.
          const isCampaignRewards = processedRewards.length > 5;
          
          if (isCampaignRewards) {
            // Campaign rewards: fetch full order data from database for each order
            const orderIds = processedRewards
              .filter(r => r && r.type === 'order' && r.data && r.data.id)
              .map(r => r.data.id);
            
            if (orderIds.length > 0) {
              // Get current language from i18n
              const currentLanguage = i18nInstance?.language || 'en-us';
              const normalizedLanguage = currentLanguage.toLowerCase().replace('_', '-');
              
              // Fetch all orders once, then match by ID (lang from CODA UI, not browser Accept-Language)
              fetch(`${API_URL}/all-orders?lang=${encodeURIComponent(normalizedLanguage)}`, {
                headers: {
                  Authorization: `Bearer ${token}`
                }
              })
                .then(res => res.json())
                .then(allOrders => {
                  // Create a map of order ID to full order data
                  const orderMap = new Map();
                  allOrders.forEach(order => {
                    orderMap.set(order.id, order);
                  });
                  
                  // Replace reward data with full order data
                  const enrichedRewards = processedRewards.map(reward => {
                    if (reward && reward.type === 'order' && reward.data && reward.data.id) {
                      const fullOrder = orderMap.get(reward.data.id);
                      if (fullOrder) {
                        return {
                          ...reward,
                          data: fullOrder
                        };
                      }
                    }
                    return reward;
                  });
                  
                  setRewards(enrichedRewards);
                  setRevealedRewards(new Array(enrichedRewards.length).fill(true)); // Auto-reveal all campaign rewards
                })
                .catch(err => {
                  console.error('Failed to fetch full order data for campaign rewards:', err);
                  // Fallback: use partial data
                  setRewards(processedRewards);
                  setRevealedRewards(new Array(processedRewards.length).fill(true));
                });
            } else {
              setRewards(processedRewards);
              setRevealedRewards(new Array(processedRewards.length).fill(true));
            }
          } else {
            // 3–5 rewards: flip layout (face-down, click to reveal) – War Effort 4 or 5 (with Research Center)
            const count = Math.max(3, Math.min(5, processedRewards.length));
            while (processedRewards.length < count) {
              processedRewards.push(null);
            }
            const toShow = processedRewards.slice(0, count);
            setRewards(toShow);
            setRevealedRewards(new Array(toShow.length).fill(false)); // Start face-down; user flips to reveal
            playSoundEffect('/sounds/hidden_rewards.mp3');
            const initialEnriched = toShow.map(r => r && r.type === 'order' && r.data ? r.data : null);
            setEnrichedRewards(initialEnriched);
          }
          
          // Grant rewards if not already granted
          if (!data.granted) {
            console.log('AfterBattle: Granting rewards...');
            fetch(`${API_URL}/battle-rewards/${gameId}/grant`, {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${token}`
              }
            })
              .then(grantRes => {
                console.log('AfterBattle: Grant response status:', grantRes.status);
                if (grantRes.ok) {
                  setRewardsGranted(true);
                } else {
                  return grantRes.json().then(err => {
                    console.error('AfterBattle: Grant error:', err);
                  });
                }
              })
              .catch(err => {
                console.error('AfterBattle: Error granting rewards:', err);
              });
          } else {
            console.log('AfterBattle: Rewards already granted');
            setRewardsGranted(true);
          }
          
          setRewardsLoading(false);
        } catch (err) {
          console.error('AfterBattle: Error fetching rewards:', err);
          if (retryCount < maxRetries) {
            console.log(`🎁 AfterBattle: Fetch error, retrying in ${retryDelay}ms... (${retryCount + 1}/${maxRetries})`);
            retryCount++;
            setTimeout(fetchRewards, retryDelay);
          } else {
            console.error('🎁 AfterBattle: Max retries reached after errors');
            setRewardsError('Failed to load rewards after multiple attempts. Please refresh the page or contact support if the issue persists.');
            setRewardsLoading(false);
          }
        }
      };
      
      fetchRewards();
    } else {
      console.log('AfterBattle: Skipping rewards fetch - gameId:', gameId, 'rewardsGranted:', rewardsGranted);
    }
  }, [gameId, rewardsGranted, isFriendlyMatch, isTournamentMatch, isCampaignTutorial, isCampaign, missionId, result, isEngageEnemyQueue, isTrainPvE, isEliteForceJoinedCountry, location.state, i18nInstance.language]);

  // Fetch opponent strategy info when a public strategy ID is available
  useEffect(() => {
    if (!opponentStrategyId) {
      return;
    }
    const token = localStorage.getItem('token');
    fetch(`${API_URL}/strategy/${opponentStrategyId}/public-info`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    })
      .then(res => {
        if (res.ok) {
          return res.json();
        }
        return res.json().then(err => {
          console.error('Strategy info error response:', err);
          throw new Error(err?.error || `strategy public-info HTTP ${res.status}`);
        });
      })
      .then(data => {
        if (data && !data.is_private) {
          setOpponentStrategy(data);
        }
      })
      .catch(err => {
        console.error('Failed to fetch opponent strategy info:', err);
      });
  }, [opponentStrategyId]);

  const handleSubscribe = async () => {
    if (!opponentStrategy || isSubscribing) return;
    
    setIsSubscribing(true);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/subscribe-strategy/${opponentStrategy.id}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (res.ok) {
        alert(t('afterBattle.subscribeSuccess'));
        setOpponentStrategy(null); // Hide the subscribe section after successful subscription
      } else {
        alert(t('afterBattle.subscribeFailed'));
      }
    } catch (err) {
      console.error('Failed to subscribe to strategy:', err);
      alert(t('afterBattle.subscribeFailed'));
    } finally {
      setIsSubscribing(false);
    }
  };

  const isExploreUnknownRunEndSummary = isExploreUnknownLoss || isExploreUnknownHomecoming;
  const exploreUnknownBossWinDarknessReductionPercent = useMemo(() => {
    if (!isExploreUnknownRun || result !== 'win') {
      return null;
    }
    const raw =
      location.state?.gameState?.exploreUnknownBossWinDarknessReductionPercent ??
      location.state?.exploreUnknownBossWinDarknessReductionPercent;
    if (raw == null) {
      return null;
    }
    const n = Number(raw);
    if (!Number.isFinite(n) || n <= 0) {
      return null;
    }
    return Math.round(n);
  }, [isExploreUnknownRun, result, location.state]);
  const showExploreUnknownBossWinDarknessReduction =
    exploreUnknownBossWinDarknessReductionPercent != null;
  const exploreUnknownBossWinDarknessMeter = useMemo(() => {
    if (!showExploreUnknownBossWinDarknessReduction) {
      return null;
    }
    const rawFrom =
      location.state?.gameState?.exploreUnknownBossWinDarknessMeterFromPct ??
      location.state?.exploreUnknownBossWinDarknessMeterFromPct;
    const rawTo =
      location.state?.gameState?.exploreUnknownBossWinDarknessMeterToPct ??
      location.state?.exploreUnknownBossWinDarknessMeterToPct;
    if (rawFrom == null || rawTo == null) {
      return null;
    }
    const from = Number(rawFrom);
    const to = Number(rawTo);
    if (!Number.isFinite(from) || !Number.isFinite(to)) {
      return null;
    }
    return { fromPct: from, toPct: to };
  }, [
    showExploreUnknownBossWinDarknessReduction,
    exploreUnknownBossWinDarknessReductionPercent,
    location.state,
  ]);
  const message = isExploreUnknownHomecoming
    ? t('afterBattle.exploreUnknownHomecomingTitle')
    : isExploreUnknownLoss
    ? t('afterBattle.exploreUnknownLossTitle')
    : result === 'win'
      ? t('afterBattle.youWon')
      : result === 'loss'
        ? t('afterBattle.youLost')
        : t('afterBattle.matchEnded');
  const warEffortHQMessage = result === 'win' ? t('afterBattle.missionSuccessful') : t('afterBattle.missionFailed');

  // Determine background: War Effort HQ uses wareffort_success/lose images; Explore Unknown loss uses Shadowverse art; otherwise win/loss images
  const backgroundImage = isWarEffortHQEnding
      ? (result === 'loss' ? '/images/wareffort_lose.jpg' : '/images/wareffort_success.jpg')
      : isExploreUnknownHomecoming
        ? '/images/homecoming.jpg'
      : isExploreUnknownLoss
        ? '/unknown_backgrounds/unknown_lost.jpg'
        : factionBattleEndPresentation
          ? factionBattleEndPresentation.imageSrc
        : (result === 'loss' ? '/images/loss.jpg' : result === 'win' ? '/images/win.jpg' : null);

  const showPvpRankOverlay =
    isEngageEnemyQueue &&
    !isFriendlyMatch &&
    !isTournamentMatch &&
    engageEnemyRankPhase === 'rank' &&
    pvpLadderData;

  const devPvpRankMock = location.state?.devPvpRankMock === true;

  const handleDevPvpSimulateWin = React.useCallback(() => {
    if (!pvpLadderData) {
      throw new Error('AfterBattle: pvpLadderData required for dev simulate win');
    }
    if (pvpLadderData.rankIndex === GENERAL_RANK_INDEX) {
      alert(t('pvpRank.devSimulateWinAtCap'));
      return;
    }
    try {
      const { snapshot } = advanceDevMockPvpWin(
        pvpLadderData.rankIndex,
        Number(pvpLadderData.stars)
      );
      setPvpLadderData(snapshot);
    } catch (err) {
      console.error('Dev PvP simulate win:', err);
      alert(err instanceof Error ? err.message : 'Simulate win failed');
    }
  }, [pvpLadderData, t]);

  const showDevPvpSimulateWin =
    devPvpRankMock &&
    result === 'win' &&
    pvpLadderData &&
    pvpLadderData.rankIndex !== GENERAL_RANK_INDEX;

  return (
    <div className="afterbattle-viewport-wrapper" data-block-global-esc-back>
      {pendingToast ? (
        <AchievementUnlockedToast
          titleId={pendingToast.titleId}
          titleName={pendingToast.titleName}
          color={pendingToast.color}
          achievementKey={pendingToast.achievementKey}
          onClose={dismissPendingToast}
        />
      ) : null}
      {/* Full-screen background image */}
      {backgroundImage && (
        <div 
          className="afterbattle-fullscreen-background"
          style={{ backgroundImage: `url(${backgroundImage})` }}
        />
      )}
      
      {/* Viewport container - 1920x1080 space that scales to fit screen */}
      <div 
        className={`afterbattle-viewport-container${isContainerMobile ? ' mobile' : ''}`}
        style={{
          width: `${containerBaseWidth}px`,
          height: `${containerBaseHeight}px`,
          transformOrigin: '0 0',
          transform: `translate(${viewportOffsetX}px, ${viewportOffsetY}px) scale(${viewportScale})`,
        }}
      >
        <div className="afterbattle-container">
          {showPvpRankOverlay && (
            <PvpRankProgressScreen
              pvpLadder={pvpLadderData}
              result={result === 'loss' ? 'loss' : 'win'}
              onProceed={() => setEngageEnemyRankPhase('rewards')}
              devSimulateWinEnabled={showDevPvpSimulateWin}
              onDevSimulateWin={handleDevPvpSimulateWin}
            />
          )}
          {/* War Effort HQ: show big mission message first, then it fades and rewards show */}
          {isWarEffortHQEnding && (
            <div
              className={`afterbattle-war-effort-message ${warEffortMessagePhase === 'rewards' ? 'afterbattle-war-effort-message-hidden' : ''}`}
              aria-live="polite"
            >
              <h1 className="afterbattle-war-effort-message-text">{warEffortHQMessage}</h1>
            </div>
          )}
          {/* Normal (non–War Effort HQ) or War Effort HQ after transition: show standard title */}
          {(!isWarEffortHQEnding || warEffortMessagePhase === 'rewards') && !showPvpRankOverlay && (
            <div className="afterbattle-victory-stack">
              <h1 className="afterbattle-victory-title">{message}</h1>
              {isExploreUnknownDarknessLoss ? (
                <p className="afterbattle-victory-subtitle">
                  {t('afterBattle.exploreUnknownDarknessLossSubtitle')}
                </p>
              ) : null}
              {showExploreUnknownXpOverlay || showExploreUnknownBossWinDarknessReduction ? (
                <div className="afterbattle-eu-victory-panels">
                  {showExploreUnknownXpOverlay ? (
                    <ExploreUnknownXpGainOverlay
                      grant={exploreUnknownXpGrant}
                      onAnimationComplete={handleExploreUnknownXpAnimationComplete}
                    />
                  ) : null}
                  {showExploreUnknownBossWinDarknessReduction ? (
                    <div className="eu-xp-overlay eu-xp-overlay--after-battle eu-xp-overlay--boss-darkness">
                      <div
                        className="eu-xp-overlay-plaque afterbattle-eu-boss-darkness-reward"
                        role="status"
                      >
                        {exploreUnknownBossWinDarknessMeter ? (
                          <ExploreUnknownDarknessMeter
                            className="explore-unknown-darkness-bar-wrap--after-battle"
                            fromPercent={exploreUnknownBossWinDarknessMeter.fromPct}
                            toPercent={exploreUnknownBossWinDarknessMeter.toPct}
                            tooltipTitle={t('exploreUnknownPage.darknessMeterTooltipTitle', {
                              percent: Math.round(exploreUnknownBossWinDarknessMeter.toPct),
                            })}
                            regionLabel={t('exploreUnknownPage.darknessMeterRegion')}
                          />
                        ) : null}
                        <p className="afterbattle-eu-boss-darkness-reduction">
                          {t('afterBattle.exploreUnknownBossWinDarknessReduction', {
                            percent: exploreUnknownBossWinDarknessReductionPercent,
                          })}
                        </p>
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}
              {showMilestoneUnlockOverlay &&
              exploreUnknownNewMilestones[milestoneUnlockIndex] != null ? (
                <ExploreUnknownMilestoneUnlockOverlay
                  key={exploreUnknownNewMilestones[milestoneUnlockIndex]}
                  milestoneLevel={exploreUnknownNewMilestones[milestoneUnlockIndex]}
                  onComplete={handleMilestoneUnlockComplete}
                />
              ) : null}
              {showLevel100DoorUnlockOverlay ? (
                <ExploreUnknownMilestoneUnlockOverlay
                  key="eu-level100-door-unlock-victory"
                  variant="doorUnlock"
                  milestoneLevel={100}
                  onComplete={handleLevel100DoorUnlockComplete}
                />
              ) : null}
              {showBonusOrderRewardModal && activeBonusOrderReward ? (
                <ExploreUnknownDarkKnowledgeRewardModal
                  key={activeBonusOrderReward.popupId}
                  popupId={activeBonusOrderReward.popupId}
                  rewardType={activeBonusOrderReward.rewardType}
                  orderId={activeBonusOrderReward.orderId}
                  amount={activeBonusOrderReward.amount}
                  onDismiss={handleBonusOrderRewardDismiss}
                />
              ) : null}
              {showRewriteFragmentMapProcModal && hasRewriteFragmentMapProcGrant ? (
                <ExploreUnknownRewriteFragmentMapProcModal
                  popupId={`eu-rewrite-fragment-map-proc-${exploreUnknownXpGrant?.rewriteExperiencePartsCollected ?? 0}`}
                  partsCollected={Number(exploreUnknownXpGrant?.rewriteExperiencePartsCollected) || 0}
                  targetParts={EXPLORE_UNKNOWN_REWRITE_EXPERIENCE_TARGET_PARTS}
                  setCompleted={exploreUnknownXpGrant?.rewriteExperienceSetCompletedFromMapProc === true}
                  newCharges={
                    exploreUnknownXpGrant?.rewriteExperienceSetCompletedFromMapProc === true
                      ? Number(exploreUnknownXpGrant?.rewriteExperienceCharges) || 0
                      : undefined
                  }
                  onDismiss={handleRewriteFragmentMapProcDismiss}
                />
              ) : null}
              {showRefinedErrayBossDropModal && exploreUnknownRefinedErrayBossDrop ? (
                <ExploreUnknownRefinedErrayBossDropModal
                  popupId={`eu-refined-erray-boss-${exploreUnknownRefinedErrayBossDrop.missionIndex}`}
                  missionIndex={exploreUnknownRefinedErrayBossDrop.missionIndex}
                  onDismiss={handleRefinedErrayBossDropDismiss}
                />
              ) : null}
              {showEpFragmentMapProcModal && hasEpFragmentMapProcGrant ? (
                <ExploreUnknownEpFragmentMapProcModal
                  popupId={`eu-ep-fragment-map-proc-${exploreUnknownXpGrant?.epFragmentMapProcBonusId ?? 'ep'}-${exploreUnknownXpGrant?.epFragmentMapProcPartsCollected ?? 0}`}
                  epBonusId={exploreUnknownXpGrant?.epFragmentMapProcBonusId}
                  partsCollected={Number(exploreUnknownXpGrant?.epFragmentMapProcPartsCollected) || 0}
                  targetParts={Number(exploreUnknownXpGrant?.epFragmentMapProcTargetParts) || 3}
                  setCompleted={exploreUnknownXpGrant?.epFragmentMapProcSetCompleted === true}
                  newCharges={
                    exploreUnknownXpGrant?.epFragmentMapProcSetCompleted === true
                      ? Number(exploreUnknownXpGrant?.epFragmentMapProcCharges) || 0
                      : undefined
                  }
                  onDismiss={handleEpFragmentMapProcDismiss}
                />
              ) : null}
            </div>
          )}
      
      {/* Rewards Display - Hidden for friendly matches, tournament matches, and during War Effort HQ message phase */}
      {isExploreUnknownRunEndSummary && euRunRewardsLoading && (
        <div className="eu-run-rewards-loading">{t('afterBattle.exploreUnknownRunRewardsLoading')}</div>
      )}
      {isExploreUnknownRunEndSummary && euRunRewardsError && (
        <div className="eu-run-rewards-error" role="alert">
          {euRunRewardsError}
        </div>
      )}
      {isExploreUnknownRunEndSummary && !euRunRewardsLoading && !euRunRewardsError && euRunRewardsSummary && (
        <ExploreUnknownRunRewardsOverview summary={euRunRewardsSummary} />
      )}
      {!(isWarEffortHQEnding && warEffortMessagePhase === 'message') && !isFriendlyMatch && !isTournamentMatch && rewardsLoading && !showPvpRankOverlay && !isExploreUnknownRunEndSummary && (
        <div style={{ marginBottom: '2rem', fontSize: '1.2rem' }}>Loading rewards...</div>
      )}
      {!(isWarEffortHQEnding && warEffortMessagePhase === 'message') && !isFriendlyMatch && !isTournamentMatch && !(isCampaign && (missionId === 'chapter_1_mission_2' || missionId === 'chapter_1_mission_4')) && rewardsError && (
        <div style={{ 
          marginBottom: '2rem', 
          fontSize: '1.2rem', 
          color: '#ff6b6b',
          padding: '1rem',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          borderRadius: '8px',
          maxWidth: '600px'
        }}>
          {rewardsError}
          <br />
          <AfterBattleButton
            variant="blue"
            onClick={() => window.location.reload()}
            style={{ marginTop: '1rem' }}
          >
            Refresh Page
          </AfterBattleButton>
        </div>
      )}
      
      {/* ✅ DAILY BOT GAME REWARD LIMIT: Display message when daily limit reached */}
      {!(isWarEffortHQEnding && warEffortMessagePhase === 'message') && !isFriendlyMatch && !isTournamentMatch && !rewardsLoading && dailyLimitReached && (
        <div style={{ 
          marginBottom: '2rem', 
          fontSize: '1.5rem', 
          color: '#ffd700',
          padding: '1.5rem',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          borderRadius: '8px',
          maxWidth: '800px',
          textAlign: 'center',
          fontWeight: 'bold'
        }}>
          Don't you think you looted enough for a day?
        </div>
      )}
      
      {/* ✅ CAMPAIGN TUTORIAL REWARD CHOICE: same layout as PvP/Black Ops rewards – two orders side by side in afterbattle-rewards-grid */}
      {!(isWarEffortHQEnding && warEffortMessagePhase === 'message') && isCampaignTutorial && result === 'win' && tutorialRewardChoice && !tutorialRewardGranted && (
        <div style={{ marginBottom: '2rem', width: '95%', maxWidth: '1350px', margin: '0 auto' }}>
          <h2 style={{ fontSize: '1.8rem', marginBottom: '0.5rem', textAlign: 'center' }}>{t('afterBattle.tutorialRewardTitle')}</h2>
          <p style={{ fontSize: '1.1rem', marginBottom: '1.5rem', opacity: 0.9, textAlign: 'center' }}>
            {tutorialRewardChoice.copiesToGrant === 3
              ? t('afterBattle.tutorialRewardPickEpic')
              : t('afterBattle.tutorialRewardPickOne')}
          </p>
          <div className={`afterbattle-rewards-grid ${tutorialRewardLoading ? 'tutorial-choice-loading' : ''}`}>
            {tutorialRewardChoice.options?.map((opt) => {
              const order = tutorialChoiceOrders?.[opt.id];
              return (
                <div
                  key={opt.id}
                  className="afterbattle-reward-card tutorial-reward-choice-card"
                  onClick={async () => {
                    if (tutorialRewardLoading) return;
                    setTutorialRewardLoading(true);
                    try {
                      const token = localStorage.getItem('token');
                      const endpoint = missionId === 'chapter_1_mission_3'
                        ? `${API_URL}/campaign-tutorial3-reward`
                        : `${API_URL}/campaign-tutorial-reward`;
                      const res = await fetch(endpoint, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          Authorization: `Bearer ${token}`
                        },
                        body: JSON.stringify({ gameId, orderId: opt.id })
                      });
                      const data = await res.json();
                      if (res.ok && data.success) {
                        setTutorialRewardGranted({ ...data.grantedOrder, grantedCount: data.grantedCount });
                      } else {
                        alert(data.error || 'Failed to claim reward');
                      }
                    } catch (err) {
                      console.error('Tutorial reward error:', err);
                      alert('Failed to claim reward');
                    } finally {
                      setTutorialRewardLoading(false);
                    }
                  }}
                  style={{
                    cursor: tutorialRewardLoading ? 'not-allowed' : 'pointer',
                    opacity: tutorialRewardLoading ? 0.7 : 1
                  }}
                >
                  {order ? (
                    <div className={`reward-order-container rarity-${((order.rarity || 'common').toLowerCase())}`} style={{ position: 'relative' }}>
                      <OrderDesign
                        order={order}
                        count={0}
                        minimal={false}
                        rarity={order.rarity}
                        lazyLoad={false}
                        gameState={null}
                      />
                      <OrderCardBubbles rarity={order.rarity || 'common'} />
                      {tutorialRewardChoice.copiesToGrant === 3 && (
                        <div style={{
                          position: 'absolute', bottom: '10px', right: '10px',
                          backgroundColor: 'rgba(255, 215, 0, 0.9)', color: '#000',
                          padding: '4px 10px', borderRadius: '6px', fontWeight: 'bold', fontSize: '1rem'
                        }}>x3</div>
                      )}
                    </div>
                  ) : (
                    <div style={{ width: '390px', height: '390px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', padding: '2rem' }}>
                      {tutorialChoiceOrders === null ? 'Loading...' : opt.name}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      {/* ✅ NEUTRAL COUNTRY CONQUEST/SUBVERSION: 3 orders to choose 1, 2, or 3 (Training Grounds) for attack strategy */}
      {!(isWarEffortHQEnding && warEffortMessagePhase === 'message') && isWarEffort && result === 'win' && neutralCountryChoice && !neutralCountryGranted && (() => {
        const ordersToKeep = typeof neutralCountryChoice.ordersToKeep === 'number' ? Math.min(3, Math.max(1, neutralCountryChoice.ordersToKeep)) : 1;
        const isMultiSelect = ordersToKeep > 1;
        const selectedIds = neutralCountrySelectedIds;
        const toggleSelection = (id) => {
          if (selectedIds.includes(id)) {
            setNeutralCountrySelectedIds((prev) => prev.filter((x) => x !== id));
          } else if (selectedIds.length < ordersToKeep) {
            setNeutralCountrySelectedIds((prev) => [...prev, id]);
          }
        };
        const submitSelection = async (orderIdsToSubmit) => {
          if (neutralCountryLoading) return;
          const missionIdForClaim = neutralCountryChoice.missionId ?? gameState?.warEffortMissionId ?? location.state?.missionId;
          if (!missionIdForClaim) {
            alert('Mission context missing');
            return;
          }
          setNeutralCountryLoading(true);
          try {
            const token = localStorage.getItem('token');
            const body = orderIdsToSubmit.length === 1 ? { gameId, orderId: orderIdsToSubmit[0], missionId: missionIdForClaim } : { gameId, orderIds: orderIdsToSubmit, missionId: missionIdForClaim };
            const res = await fetch(`${API_URL}/war-effort/neutral-country-reward`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
              body: JSON.stringify(body)
            });
            const data = await res.json();
            if (res.ok && data.success) {
              const grantedOrders = Array.isArray(data.grantedOrders) ? data.grantedOrders : [];
              const localizedGrantedOrders = await enrichOrdersWithLocalizedNames(
                grantedOrders,
                i18nInstance.language,
                token
              );
              setNeutralCountryGranted(true);
              setNeutralCountryGrantedOrders(localizedGrantedOrders);
              setNeutralCountrySelectedIds([]);
              if (data.building?.type === 'building' && data.building?.data?.building_id) {
                setNeutralCountryGrantedBuilding(data.building);
              }
            } else {
              alert(data.error || 'Failed to claim reward');
            }
          } catch (err) {
            console.error('Neutral country reward error:', err);
            alert('Failed to claim reward');
          } finally {
            setNeutralCountryLoading(false);
          }
        };
        const handleCardClick = (opt) => {
          if (isMultiSelect) {
            toggleSelection(opt.id);
          } else {
            submitSelection([opt.id]);
          }
        };
        const canConfirm = selectedIds.length >= 1 && selectedIds.length <= ordersToKeep;
        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: 24,
              marginBottom: '2rem'
            }}
          >
            <h3 style={{ margin: '0 0 8px 0', color: '#fff' }}>
              {isMultiSelect
                ? t('afterBattle.chooseUpToOrdersForAttackStrategy', { count: ordersToKeep })
                : t('afterBattle.chooseOneOrderForAttackStrategy')}
            </h3>
            <p style={{ fontSize: 12, color: '#aaa', marginBottom: 20 }}>{t('warEffort.theseOrdersMissionOnly')}</p>
            <div
              style={{
                display: 'flex',
                flexWrap: 'nowrap',
                gap: 40,
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              {neutralCountryChoice.options?.map((opt) => {
                const order = neutralCountryChoiceOrders?.[opt.id] ?? opt;
                const isSelected = selectedIds.includes(opt.id);
                return (
                  <div
                    key={opt.id}
                    style={{
                      width: '390px',
                      height: '390px',
                      flexShrink: 0,
                      cursor: neutralCountryLoading ? 'not-allowed' : 'pointer',
                      position: 'relative',
                      overflow: 'visible',
                      outline: isMultiSelect && isSelected ? '4px solid #4ECDC4' : 'none',
                      borderRadius: 8
                    }}
                    onClick={() => handleCardClick(opt)}
                  >
                    <div
                      className={`reward-order-container rarity-${((order?.rarity || opt.rarity || 'common').toLowerCase())}`}
                      style={{
                        width: '390px',
                        height: '390px',
                        position: 'relative',
                        margin: 0,
                        padding: 0
                      }}
                    >
                      {order && (order.name || order.id) ? (
                        <>
                          <OrderDesign
                            order={order}
                            minimal={false}
                            noMargin={true}
                            gameState={null}
                          />
                          <OrderCardBubbles rarity={order.rarity || opt.rarity || 'common'} />
                        </>
                      ) : (
                        <div
                          style={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'transparent',
                            color: '#aaa',
                            fontSize: 14
                          }}
                        >
                          Loading…
                        </div>
                      )}
                    </div>
                    {isMultiSelect && isSelected && (
                      <div style={{ position: 'absolute', top: 8, right: 8, background: '#4ECDC4', color: '#000', padding: '4px 8px', borderRadius: 4, fontWeight: 'bold' }}>
                        Selected
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {isMultiSelect && (
              <div className="afterbattle-neutral-country-actions">
                {ordersToKeep === 3 && (
                  <AfterBattleButton
                    variant="golden"
                    onClick={() => submitSelection(neutralCountryChoice.options.map((o) => o.id))}
                    disabled={neutralCountryLoading}
                  >
                    {t('afterBattle.keepAllThree')}
                  </AfterBattleButton>
                )}
                <AfterBattleButton
                  variant="golden"
                  onClick={() => submitSelection(selectedIds)}
                  disabled={!canConfirm || neutralCountryLoading}
                >
                  {t('afterBattle.confirmSelection', { selected: selectedIds.length, total: ordersToKeep })}
                </AfterBattleButton>
              </div>
            )}
          </div>
        );
      })()}
      
      {/* ✅ NEUTRAL COUNTRY: Show confirmation after claiming - orders added to attack strategy for this mission */}
      {!(isWarEffortHQEnding && warEffortMessagePhase === 'message') && isWarEffort && result === 'win' && neutralCountryGranted && (
        <div style={{
          marginBottom: '2rem',
          padding: '2rem',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          borderRadius: '12px',
          maxWidth: '500px',
          margin: '0 auto',
          textAlign: 'center'
        }}>
          <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: '#fff' }}>{t('afterBattle.ordersAddedToAttackStrategyTitle')}</h2>
          <p style={{ fontSize: '1rem', marginBottom: '1rem', color: '#ccc' }}>
            {neutralCountryGrantedOrders?.length > 0
              ? t('afterBattle.neutralCountryOrdersAddedNamed', {
                  orderNames: neutralCountryGrantedOrders.map((o) => o.name).join(', '),
                  count: neutralCountryGrantedOrders.length
                })
              : t('afterBattle.neutralCountryOrdersAddedFallback')}
          </p>
          <p style={{ fontSize: '0.9rem', color: '#aaa', marginBottom: '1rem' }}>
            {t('afterBattle.neutralCountryOpenAttackStrategyModal')}
          </p>
          {neutralCountryGrantedBuilding?.data?.building_id && (() => {
            const buildingId = neutralCountryGrantedBuilding.data.building_id;
            const buildingDisplayNames = {
              eclipse_array: 'Eclipse Array',
              command_center: 'Command Center',
              cattle_ranch: 'Cattle Farm',
              quarry: 'Quarry',
              sewers: 'Sewers',
              portal: 'Portal',
              lavender_field: 'Lavender Farm',
              armory: 'Armory',
              drone_hangar: 'Drone Hangar',
              communications_hub: 'Communications Hub',
              intelligence_agency: 'Intelligence Agency',
              bank: 'Omniforge',
              research_center: 'Research Center',
              hospital: 'Hospital',
              hall: 'Hall of Ascension',
              airfield: 'Airfield',
              depot: 'Depot',
              trade: 'Trade Forum',
              edc: 'Energy Distribution Center'
            };
            const buildingImages = {
              eclipse_array: '/images/eclipse.png',
              command_center: '/images/command.png',
              cattle_ranch: '/images/cattle.png',
              quarry: '/images/quarry.png',
              sewers: '/images/sewers.png',
              portal: '/images/portal.png',
              lavender_field: '/images/lavender.png',
              armory: '/images/armory.png',
              drone_hangar: '/images/dronehangar.png',
              communications_hub: '/images/comhub.png',
              intelligence_agency: '/images/intel.png',
              bank: '/images/bank.png',
              research_center: '/images/research.png',
              hospital: '/images/hospital.png',
              hall: '/images/hall.png',
              airfield: '/images/airfield.png',
              depot: '/images/depot.png',
              trade: '/images/trade.png',
              edc: '/images/edc.png'
            };
            const displayName = buildingDisplayNames[buildingId] || buildingId.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
            const imgSrc = buildingImages[buildingId] || `/images/${buildingId}.png`;
            return (
              <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid rgba(255,255,255,0.2)' }}>
                <h3 style={{ fontSize: '1.2rem', marginBottom: '0.75rem', color: '#fff' }}>{t('warEffort.hqBuildingReward')}</h3>
                <img src={imgSrc} alt={displayName} style={{ width: 120, height: 120, objectFit: 'contain', marginBottom: '0.5rem' }} />
                <p style={{ fontSize: '1rem', color: '#4ECDC4', margin: 0 }}>{displayName}</p>
              </div>
            );
          })()}
        </div>
      )}
      
      {/* ✅ CAMPAIGN TUTORIAL: Second playthrough - no rewards, show well done summary + buttons */}
      {!(isWarEffortHQEnding && warEffortMessagePhase === 'message') && isCampaignTutorial && (missionId === 'chapter_1_mission_1' || missionId === 'chapter_1_mission_3') && result === 'win' && !tutorialRewardChoice && !rewardsLoading && rewards.length === 0 && rewardsGranted && (
        <div style={{
          marginBottom: '2rem',
          padding: '2rem',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          borderRadius: '12px',
          maxWidth: '500px',
          margin: '0 auto',
          textAlign: 'center'
        }}>
          <p style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>
            {missionId === 'chapter_1_mission_1' ? t('afterBattle.wellDoneSummaryMission1') : t('afterBattle.wellDoneSummaryMission3')}
          </p>
          <p style={{ fontSize: '0.95rem', opacity: 0.9, marginBottom: '1rem' }}>
            {t('afterBattle.tutorialAlreadyCompletedNoRewards')}
          </p>
          <div className="afterbattle-completion-actions">
            <AfterBattleButton
              variant="golden"
              onClick={() => navigate(missionId === 'chapter_1_mission_1' ? '/campaign-battle/chapter_1_mission_2' : '/campaign-battle/chapter_1_mission_4')}
            >
              {t('afterBattle.nextTutorialMission')}
            </AfterBattleButton>
            <AfterBattleButton
              variant="blue"
              onClick={() => goToCampaignTutorialHub()}
            >
              {t('afterBattle.backToTutorial')}
            </AfterBattleButton>
          </div>
        </div>
      )}

      {/* ✅ CAMPAIGN MISSION 2 & 4: Victory - strategy selection reward (TutorialMessageBox portal) */}
      {!(isWarEffortHQEnding && warEffortMessagePhase === 'message') && isCampaign && isStrategyRewardMission && result === 'win' && !rewardsLoading && !m2StrategyGranted && m2StrategyChoices && m2StrategyChoices.length > 0 && createPortal(
        <TutorialMessageBox
          image="/images/tutorial_welcome.png"
          text={t('afterBattle.missionCompleteChooseStrategy')}
          choices={m2StrategyChoices}
          choiceLabelPrefix="welcome.strategyPreference"
          onSelect={handleM2StrategySelect}
          onContinue={() => {}}
          disabled={m2StrategyLoading}
        />,
        document.body
      )}

      {/* CAMPAIGN MISSION 2 & 4: Strategy claimed - show confirmation + well done summary + buttons */}
      {!(isWarEffortHQEnding && warEffortMessagePhase === 'message') && isCampaign && isStrategyRewardMission && result === 'win' && m2StrategyGranted && (
        <div style={{
          marginBottom: '2rem',
          padding: '2rem',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          borderRadius: '12px',
          maxWidth: isTutorialMission4Complete ? '560px' : '500px',
          margin: '0 auto',
          textAlign: 'center'
        }}>
          <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>{t('afterBattle.strategyUnlocked')}</h2>
          <p style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>
            {t('afterBattle.strategyAddedToCollection', { strategyName: m2StrategyGranted.strategyName })}
          </p>
          {m2StrategyGranted.grantedOrders?.length > 0 && (
            <p style={{ fontSize: '0.95rem', marginBottom: '0.5rem', opacity: 0.8 }}>
              {t('afterBattle.ordersAddedToCollection', { count: m2StrategyGranted.grantedOrders.length })}
            </p>
          )}
          <p style={{ fontSize: '1rem', marginBottom: '1rem', opacity: 0.9 }}>
            {t(strategyRewardWellDoneKey)}
          </p>
          {isTutorialMission4Complete && (
            <p style={{ fontSize: '0.95rem', marginBottom: '1.25rem', opacity: 0.85, lineHeight: 1.5 }}>
              {t('afterBattle.tutorialCompleteMission4ExploreUnknownIntro')}
            </p>
          )}
          {renderStrategyRewardCompletionActions()}
        </div>
      )}

      {/* CAMPAIGN MISSION 2 & 4: No choices available (already claimed or owns all) - well done summary + buttons */}
      {!(isWarEffortHQEnding && warEffortMessagePhase === 'message') && isCampaign && isStrategyRewardMission && result === 'win' && !rewardsLoading && !m2StrategyGranted && m2StrategyChoices && m2StrategyChoices.length === 0 && (
        <div style={{
          marginBottom: '2rem',
          padding: '2rem',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          borderRadius: '12px',
          maxWidth: isTutorialMission4Complete ? '560px' : '500px',
          margin: '0 auto',
          textAlign: 'center'
        }}>
          <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>{t('afterBattle.missionComplete')}</h2>
          <p style={{ fontSize: '1.1rem', marginBottom: '1rem' }}>
            {t(strategyRewardWellDoneKey)}
          </p>
          {isTutorialMission4Complete && (
            <p style={{ fontSize: '0.95rem', marginBottom: '1.25rem', opacity: 0.85, lineHeight: 1.5 }}>
              {t('afterBattle.tutorialCompleteMission4ExploreUnknownIntro')}
            </p>
          )}
          {renderStrategyRewardCompletionActions()}
        </div>
      )}
      
      {/* ✅ CAMPAIGN TUTORIAL: Show granted reward after selection + well done summary + buttons */}
      {!(isWarEffortHQEnding && warEffortMessagePhase === 'message') && isCampaignTutorial && result === 'win' && tutorialRewardGranted && (
        <div style={{
          marginBottom: '2rem',
          padding: '2rem',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          borderRadius: '12px',
          maxWidth: '500px',
          margin: '0 auto',
          textAlign: 'center'
        }}>
          <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>{t('afterBattle.tutorialRewardClaimedTitle')}</h2>
          <p style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>
            {tutorialRewardGranted.grantedCount > 1
              ? t('afterBattle.tutorialRewardAddedToCollectionMultiple', {
                  count: tutorialRewardGranted.grantedCount,
                  name: tutorialRewardGranted.name,
                })
              : t('afterBattle.tutorialRewardAddedToCollection', { name: tutorialRewardGranted.name })}
          </p>
          <p style={{ fontSize: '1rem', marginBottom: '1rem', opacity: 0.9 }}>
            {missionId === 'chapter_1_mission_1' ? t('afterBattle.wellDoneSummaryMission1') : t('afterBattle.wellDoneSummaryMission3')}
          </p>
          <div className="afterbattle-completion-actions">
            <AfterBattleButton
              variant="golden"
              onClick={() => {
                const nextMission = missionId === 'chapter_1_mission_1' ? 'chapter_1_mission_2' : 'chapter_1_mission_4';
                navigate(`/campaign-battle/${nextMission}`);
              }}
            >
              {t('afterBattle.nextTutorialMission')}
            </AfterBattleButton>
            <AfterBattleButton
              variant="blue"
              onClick={() => goToCampaignTutorialHub()}
            >
              {t('afterBattle.backToTutorial')}
            </AfterBattleButton>
          </div>
        </div>
      )}
      
      {!(isWarEffortHQEnding && warEffortMessagePhase === 'message') && !isFriendlyMatch && !isTournamentMatch && !isExploreUnknownRunEndSummary && !rewardsLoading && rewards.length > 0 && !dailyLimitReached && !tutorialRewardChoice && !neutralCountryChoice && (
        <div
          className="afterbattle-rewards-wrap"
          style={{
          marginBottom: '2rem',
          width: '95%',
          maxWidth: rewards.length > 5 ? '1800px' : (rewards.length >= 4 ? '1680px' : '1350px'),
          margin: '0 auto'
        }}>
          {/* ✅ CAMPAIGN REWARDS: Grid layout only for 6+ order cards (all orders). 3–5 cards use flip layout so mixed types (order, billy_bucks, building, etc.) render correctly (e.g. War Effort + Research Center = 5). */}
          {rewards.length > 5 ? (
            // Campaign rewards: grid layout - show ALL rewards as full order cards (matching Strategy.js style)
            <div className="strategy-orders-grid">
              {rewards.map((reward, index) => {
                if (!reward || !reward.data) return null;
                
                const order = reward.data;
                
                return (
                  <div
                    key={index}
                    className={`reward-order-container rarity-${((order.rarity || 'common').toLowerCase())}`}
                    style={{
                      position: 'relative',
                      cursor: 'default'
                    }}
                  >
                    <OrderDesign
                      order={order}
                      count={0}
                      minimal={false}
                      rarity={order.rarity}
                      lazyLoad={false}
                      gameState={null}
                    />
                    <OrderCardBubbles rarity={order.rarity || 'common'} />
                  </div>
                );
              })}
            </div>
          ) : (
            // Regular, War Effort (4), or War Effort + Research Center (5): flip layout - handles order, billy_bucks, building, duplicator, etc.
            <div className="afterbattle-rewards-grid">
            {Array.from({ length: rewards.length }, (_, index) => index).map((index) => {
            const reward = rewards[index];
            const isRevealed = revealedRewards[index] === true;
            
            return (
              <div
                key={index}
                className={`afterbattle-reward-card${isRevealed ? ' afterbattle-reward-card--revealed' : ''}`}
                onClick={async () => {
                  if (!isRevealed) {
                    setRevealedRewards(prev => {
                      const newState = prev.length >= rewards.length ? [...prev] : [...prev, ...Array(rewards.length - prev.length).fill(false)];
                      newState[index] = true;
                      return newState;
                    });
                    if (reward) {
                      const isLegendaryOrder = reward.type === 'order' && (reward.data?.rarity || '').toLowerCase() === 'legendary';
                      playSoundEffect(isLegendaryOrder ? '/sounds/legendary_reveal.mp3' : '/sounds/order_reveal.mp3');
                      // Backend already enriches rewards with complete data
                      if (reward.type === 'order' && reward.data && reward.data.id) {
                        setEnrichedRewards(prev => {
                          const newState = prev.length >= rewards.length ? [...prev] : [...prev, ...Array(rewards.length - prev.length).fill(null)];
                          newState[index] = reward.data;
                          return newState;
                        });
                      }
                    }
                  }
                }}
                style={{
                  height: '390px',
                  perspective: '1000px',
                  cursor: !isRevealed ? 'pointer' : 'default',
                  position: 'relative',
                  overflow: 'visible',
                  width: '390px'
                }}
              >
                <div
                  className="afterbattle-reward-flip"
                  style={{
                    width: '390px',
                    height: '390px',
                    position: 'relative',
                    transformStyle: 'preserve-3d',
                    transition: 'transform 0.6s',
                    transform: isRevealed ? 'rotateY(180deg)' : 'rotateY(0deg)'
                  }}
                >
                  {/* Front side - Question mark with video */}
                  <div
                    className="afterbattle-reward-face afterbattle-reward-face--front"
                    style={{
                    position: 'absolute',
                    width: '390px',
                    height: '390px',
                    left: '50%',
                    transform: 'translateX(-50%) rotateY(0deg)',
                    padding: '20px 5px',
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    border: '2px solid #C0C0C0',
                    borderRadius: '8px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    boxSizing: 'border-box',
                    backfaceVisibility: 'hidden',
                    WebkitBackfaceVisibility: 'hidden',
                    overflow: 'hidden',
                    boxShadow: '0 0 8px rgba(200, 200, 200, 0.6), inset 0 0 10px rgba(255, 255, 255, 0.1)'
                  }}>
                    <video
                      ref={el => { videoRefs.current[index] = el; }}
                      poster="/images/reward_intel.jpg"
                      autoPlay
                      loop
                      muted
                      playsInline
                      style={{
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        top: 0,
                        left: 0,
                        zIndex: 1,
                        pointerEvents: 'none',
                        backgroundColor: 'rgba(0, 0, 0, 0.8)'
                      }}
                      onCanPlay={() => {
                        const video = videoRefs.current[index];
                        if (video && !revealedRewards[index]) {
                          video.play().catch(err => {
                            console.error(`Error playing video ${index}:`, err);
                          });
                        }
                      }}
                    >
                      <source src="/images/reward_animation.mp4" type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  </div>
                  
                  {/* Back side - Reward content, or empty placeholder when slot was padded */}
                  {(reward || (isRevealed && !reward)) && (
                    <div
                      className={`afterbattle-reward-face afterbattle-reward-face--back${
                        reward?.type === 'order' ? ' afterbattle-reward-face--back-order' : ''
                      }`}
                      style={{
                      position: 'absolute',
                      width: '390px',
                      height: '390px',
                      left: '50%',
                      transform: 'translateX(-50%) rotateY(180deg)',
                      padding: reward && enrichedRewards[index] && reward.type === 'order' ? '0' : '20px 5px',
                      backgroundColor: !reward ? 'rgba(0, 0, 0, 0.6)' : (reward.type === 'billy_bucks' ? '#1a1a1a' : (enrichedRewards[index] && reward.type === 'order' ? 'transparent' : 'rgba(0, 0, 0, 0.8)')),
                      border: 'none',
                      borderRadius: enrichedRewards[index] && reward.type === 'order' ? '0' : '8px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      boxSizing: 'border-box',
                      backfaceVisibility: 'hidden',
                      WebkitBackfaceVisibility: 'hidden',
                      overflow: 'visible'
                    }}>
                      {isRevealed && (reward ? (
                  <>
                    {reward.type === 'order' && (
                      <>
                        {enrichedRewards[index] ? (
                          // Show full order card with OrderDesign
                          <div
                            className="afterbattle-reward-order-shell"
                            style={{ 
                            width: '100%', 
                            height: '100%', 
                            position: 'relative',
                            overflow: 'visible',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            margin: 0,
                            padding: 0
                          }}>
                            <div className={`reward-order-container rarity-${((enrichedRewards[index].rarity || reward.data?.rarity || 'common').toLowerCase())}`} style={{
                              width: '390px',
                              height: '390px',
                              position: 'relative',
                              margin: 0,
                              padding: 0
                            }}>
                              <OrderDesign
                                key={`reward-${index}-${isRevealed ? 'revealed' : 'hidden'}`}
                                order={enrichedRewards[index]}
                                count={0}
                                minimal={false}
                                rarity={enrichedRewards[index].rarity || reward.data?.rarity}
                                lazyLoad={!isRevealed}
                                noMargin={true}
                                gameState={null}
                              />
                              <OrderCardBubbles rarity={enrichedRewards[index].rarity || reward.data?.rarity || 'common'} />
                            </div>
                          </div>
                        ) : (
                          // Fallback: show text while loading
                          <div className="afterbattle-reward-reveal-stack" style={{ textAlign: 'center', color: 'white', padding: '20px' }}>
                            <div style={{ fontSize: '1.2rem', fontWeight: 'bold', marginBottom: '10px' }}>
                              {reward.data?.name || 'Loading...'}
                            </div>
                            <div style={{ fontSize: '0.9rem', textTransform: 'capitalize', color: '#ccc' }}>
                              {reward.data?.rarity || ''} Order
                            </div>
                          </div>
                        )}
                      </>
                    )}
                    {reward.type === 'duplicator' && (
                      <div
                        className="afterbattle-reward-reveal-stack"
                        style={{ 
                        textAlign: 'center', 
                        color: 'white', 
                        padding: '20px',
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        position: 'relative'
                      }}>
                        <div style={{ 
                          position: 'relative',
                          width: '240px',
                          height: '240px',
                          marginBottom: '20px',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center'
                        }}>
                          <img 
                            src="/images/duplicator.jpg" 
                            alt="Duplicator" 
                            style={{ 
                              width: '240px', 
                              height: '240px', 
                              objectFit: 'contain',
                              position: 'relative'
                            }}
                            onError={(e) => {
                              // Fallback to text if image fails to load
                              e.target.style.display = 'none';
                            }}
                          />
                          {/* Rarity line at the bottom of the image */}
                          {reward.data?.rarity && (() => {
                            const rarity = reward.data.rarity.toLowerCase();
                            const rarityColors = {
                              'legendary': '#FF8C00',  // Golden
                              'epic': '#9B59B6',       // Purple
                              'rare': '#4A90E2',       // Blue
                              'common': '#808080'       // Gray
                            };
                            const lineColor = rarityColors[rarity] || '#808080';
                            
                            return (
                              <div style={{
                                position: 'absolute',
                                bottom: 0,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                width: '240px',
                                height: '8px',
                                backgroundColor: lineColor,
                                borderRadius: '2px'
                              }} />
                            );
                          })()}
                        </div>
                        <div style={{ fontSize: '1.2rem', fontWeight: 'bold', marginBottom: '10px' }}>
                          {reward.data.name}
                        </div>
                        <div style={{ fontSize: '0.9rem', textTransform: 'capitalize', color: '#ccc' }}>
                          {reward.data.rarity} Duplicator
                        </div>
                      </div>
                    )}
                    {reward.type === 'feature' && (() => {
                      // Get image path based on featureKey and category
                      const getFeatureImage = (category, featureKey) => {
                        if (category === 'preparation_bonus') {
                          if (featureKey === 'plan_ahead') return '/images/plan_ahead.jpg';
                          if (featureKey === 'black_market') return '/images/black_market_dossier.jpg';
                          if (featureKey === 'team_leader') return '/images/team_leader_briefing.jpg';
                        } else if (category === 'diplomatic_accords') {
                          if (featureKey === 'strategic_coordination') return '/images/strategic_coordination.jpg';
                          if (featureKey === 'public_relations') return '/images/public_relations.jpg';
                          if (featureKey === 'emergency_protocol') return '/images/emergency_protocol.jpg';
                          if (featureKey === 'command_directive') return '/images/command_directive.jpg';
                        }
                        return null;
                      };
                      
                      const imagePath = getFeatureImage(reward.data.category, reward.data.featureKey);
                      
                      return (
                        <div
                          className="afterbattle-reward-reveal-stack"
                          style={{ textAlign: 'center', color: 'white', padding: '20px', width: '100%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                          {imagePath && (
                            <img 
                              src={imagePath} 
                              alt={reward.data.category === 'diplomatic_accords' ? 'Diplomatic Accord' : 'Preparation Bonus'} 
                              style={{ width: '240px', height: '240px', marginBottom: '20px', objectFit: 'contain' }}
                              onError={(e) => {
                                // Fallback to text if image fails to load
                                e.target.style.display = 'none';
                              }}
                            />
                          )}
                          <div style={{ fontSize: '1.2rem', fontWeight: 'bold', marginBottom: '10px' }}>
                            {reward.data.category === 'diplomatic_accords' ? 'Diplomatic Accord' : 'Preparation Bonus'}
                          </div>
                          <div style={{ fontSize: '0.9rem', color: '#ccc' }}>
                            {reward.data.featureKey}
                          </div>
                        </div>
                      );
                    })()}
                    {reward.type === 'avatar' && (() => {
                      // Extract avatar name from metadata or unlockKey
                      let avatarName = null;
                      if (reward.data?.metadata?.avatar_name) {
                        avatarName = reward.data.metadata.avatar_name;
                      } else if (reward.data?.unlockKey) {
                        // Remove 'avatar_' prefix if present
                        avatarName = reward.data.unlockKey.startsWith('avatar_') 
                          ? reward.data.unlockKey.substring(7) 
                          : reward.data.unlockKey;
                      }
                      const avatarImagePath = getAvatarImage(avatarName);
                      
                      return (
                        <div
                          className="afterbattle-reward-reveal-stack"
                          style={{ textAlign: 'center', color: 'white', padding: '20px', width: '100%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                          <img 
                            src={avatarImagePath} 
                            alt={reward.data.name || 'Avatar'} 
                            style={{ width: '240px', height: '240px', marginBottom: '20px', objectFit: 'contain' }}
                            onError={(e) => {
                              // Fallback to default avatar if image fails to load
                              e.target.src = '/images/human_1.jpg';
                            }}
                          />
                          <div style={{ fontSize: '1.2rem', fontWeight: 'bold', marginBottom: '10px' }}>
                            Avatar Unlocked
                          </div>
                          <div style={{ fontSize: '0.9rem', color: '#ccc' }}>
                            {reward.data.name}
                          </div>
                        </div>
                      );
                    })()}
                    {reward.type === 'emote' && (
                      <div
                        className="afterbattle-reward-reveal-stack"
                        style={{ textAlign: 'center', color: 'white', padding: '20px', width: '100%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                        <img 
                          src="/images/emotes.jpg" 
                          alt="Emote" 
                          style={{ width: '240px', height: '240px', marginBottom: '20px' }}
                        />
                        <div style={{ fontSize: '1.2rem', color: '#ccc' }}>
                          {reward.data.speechBubble || reward.data.name}
                        </div>
                      </div>
                    )}
                    {reward.type === 'flashlight_color' && (
                      <div
                        className="afterbattle-reward-reveal-stack afterbattle-reward-reveal-stack--flashlight"
                        style={{ textAlign: 'center', color: 'white', padding: '20px', width: '100%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                        <span
                          style={{
                            display: 'block',
                            width: 120,
                            height: 120,
                            marginBottom: 20,
                            borderRadius: '50%',
                            border: '2px solid rgba(255,255,255,0.3)',
                            background: buildFlashlightSwatchBackground(normalizeFlashlightColorForDisplay(reward.data?.color)),
                          }}
                          aria-hidden
                        />
                        <div style={{ fontSize: '1.05rem', fontWeight: 'bold' }}>
                          {t('afterBattle.flashlightUnlocked', {
                            color: t(`profile.flashlight.colors.${normalizeFlashlightColorForDisplay(reward.data?.color)}`),
                          })}
                        </div>
                      </div>
                    )}
                    {reward.type === 'building' && (() => {
                      const buildingId = reward.data?.building_id || '';
                      const buildingDisplayNames = {
                        powerplant: 'Powerplant',
                        eclipse_array: 'Eclipse Array',
                        eclipse_array_upgraded: 'Upgraded Eclipse Array',
                        command_center: 'Command Center',
                        depot: 'Depot',
                        cattle_ranch: 'Cattle Farm',
                        cattle_ranch_upgraded: 'Upgraded Cattle Farm',
                        quarry: 'Quarry',
                        quarry_upgraded: 'Upgraded Quarry',
                        sewers: 'Sewers',
                        sewers_upgraded: 'Upgraded Sewers',
                        portal: 'Portal',
                        portal_upgraded: 'Upgraded Portal',
                        lavender_field: 'Lavender Farm',
                        lavender_field_upgraded: 'Upgraded Lavender Farm',
                        training_grounds: 'Training Grounds',
                        barracks: 'Barracks',
                        armory: 'Armory',
                        drone_hangar: 'Drone Hangar',
                        communications_hub: 'Communications Hub',
                        intelligence_agency: 'Intelligence Agency',
                        bank: 'Omniforge',
                        research_center: 'Research Center',
                        hospital: 'Hospital',
                        hospital_upgraded: 'Upgraded Hospital',
                        hall: 'Hall of Ascension',
                        hall_upgraded: 'Upgraded Hall of Ascension',
                        airfield: 'Airfield',
                        trade: 'Trade Forum',
                        trade_upgraded: 'Upgraded Trade Forum',
                        edc: 'Energy Distribution Center',
                        edc_upgraded: 'Upgraded Energy Distribution Center'
                      };
                      const displayName = buildingDisplayNames[buildingId] || buildingId.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                      const buildingImages = {
                        powerplant: '/images/powerplant.png',
                        eclipse_array: '/images/array.png',
                        eclipse_array_upgraded: '/images/array_upgraded.png',
                        command_center: '/images/command.png',
                        depot: '/images/depot.png',
                        cattle_ranch: '/images/cattle.png',
                        cattle_ranch_upgraded: '/images/cattle_upgraded.png',
                        quarry: '/images/stone.png',
                        quarry_upgraded: '/images/stone_upgraded.png',
                        sewers: '/images/sewers.png',
                        sewers_upgraded: '/images/sewers_upgraded.png',
                        portal: '/images/erray.png',
                        portal_upgraded: '/images/erray_updated.png',
                        lavender_field: '/images/lavender.png',
                        lavender_field_upgraded: '/images/lavender_upgraded.png',
                        training_grounds: '/images/training.png',
                        barracks: '/images/barracks.png',
                        armory: '/images/armory.png',
                        drone_hangar: '/images/dronehangar.png',
                        communications_hub: '/images/comhub.png',
                        intelligence_agency: '/images/intel.png',
                        bank: '/images/bank.png',
                        research_center: '/images/research.png',
                        hospital: '/images/hospital.png',
                        hospital_upgraded: '/images/hospital_upgraded.png',
                        hall: '/images/hall.png',
                        hall_upgraded: '/images/hall_upgraded.png',
                        airfield: '/images/airfield.png',
                        trade: '/images/trade.png',
                        trade_upgraded: '/images/trade_upgraded.png',
                        edc: '/images/edc.png',
                        edc_upgraded: '/images/edc_upgrade.png'
                      };
                      const imgSrc = buildingImages[buildingId] || (buildingId ? `/images/${buildingId}.png` : '/images/command.png');
                      return (
                        <div
                          className="afterbattle-reward-reveal-stack"
                          style={{ textAlign: 'center', color: 'white', padding: '20px', width: '100%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                          <img
                            src={imgSrc}
                            alt={displayName}
                            style={{ width: '240px', height: '240px', marginBottom: '20px', objectFit: 'contain' }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                          <div style={{ fontSize: '1.2rem', fontWeight: 'bold', marginBottom: '10px' }}>
                            HQ Building Unlocked
                          </div>
                          <div style={{ fontSize: '1rem', color: '#ccc' }}>
                            {displayName}
                          </div>
                        </div>
                      );
                    })()}
                    {reward.type === 'billy_bucks' && (
                      <div
                        className="afterbattle-reward-reveal-stack afterbattle-reward-reveal-stack--currency"
                        style={{ textAlign: 'center', color: 'white', padding: '20px', width: '100%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                        <img 
                          src="/images/billy_bucks.png" 
                          alt="Billy Bucks" 
                          style={{ width: '240px', height: '240px', marginBottom: '20px' }}
                        />
                        <div style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '10px' }}>
                          {reward.amount}
                        </div>
                        <div style={{ fontSize: '1.2rem', color: '#ccc' }}>
                          Billy Bucks
                        </div>
                      </div>
                    )}
                    {reward.type === 'resource' && (() => {
                      // Map resource type to image path
                      const getResourceImage = (resourceType) => {
                        if (!resourceType) {
                          console.warn('Resource reward missing resourceType:', reward);
                          return '/images/resource_biomass.png';
                        }
                        
                        const resourceMap = {
                          'lavender': '/images/resource_lavender.png',
                          'biomass': '/images/resource_biomass.png',
                          'food_rations': '/images/resource_foodrations.png',
                          'stone': '/images/resource_stone.png',
                          'erray': '/images/resource_erray.png',
                          'energy': '/images/powerplant.png'
                        };
                        
                        const imagePath = resourceMap[resourceType.toLowerCase()] || resourceMap[resourceType] || '/images/resource_biomass.png';
                        console.log(`Resource reward: type=${resourceType}, image=${imagePath}`);
                        return imagePath;
                      };
                      
                      // Format resource name for display
                      const getResourceName = (resourceType) => {
                        if (!resourceType) return 'Resource';
                        
                        const nameMap = {
                          'lavender': 'Lavender',
                          'biomass': 'Biomass',
                          'food_rations': 'Food Rations',
                          'stone': 'Stone',
                          'erray': 'Erray',
                          'energy': 'Energy'
                        };
                        
                        return nameMap[resourceType.toLowerCase()] || nameMap[resourceType] || resourceType;
                      };
                      
                      // Get resource type - check multiple possible locations in reward object
                      const resourceType = reward.resourceType || reward.data?.resourceType || reward.type;
                      const amount = reward.amount || reward.data?.amount || 0;
                      
                      if (!resourceType || resourceType === 'resource') {
                        console.warn('Resource reward missing valid resourceType:', reward);
                      }
                      
                      return (
                        <div
                          className="afterbattle-reward-reveal-stack afterbattle-reward-reveal-stack--currency"
                          style={{ textAlign: 'center', color: 'white', padding: '20px', width: '100%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                          <img 
                            src={getResourceImage(resourceType)} 
                            alt={getResourceName(resourceType)} 
                            style={{ width: '240px', height: '240px', marginBottom: '20px', objectFit: 'contain' }}
                            onError={(e) => {
                              console.error(`Failed to load resource image for type: ${resourceType}`);
                              // Fallback to biomass if image fails to load
                              e.target.src = '/images/resource_biomass.png';
                            }}
                          />
                          <div style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '10px' }}>
                            {amount}
                          </div>
                          <div style={{ fontSize: '1.2rem', color: '#ccc' }}>
                            {getResourceName(resourceType)}
                          </div>
                        </div>
                      );
                    })()}
                      </>
                    ) : (
                      <div style={{ color: '#888', fontSize: '1rem', textAlign: 'center' }}>No reward</div>
                    ))}
                  </div>
                  )}
                </div>
              </div>
            );
          })}
            </div>
          )}
        </div>
      )}
      
      {!(isWarEffortHQEnding && warEffortMessagePhase === 'message') && !(
        (isCampaignTutorial && (missionId === 'chapter_1_mission_1' || missionId === 'chapter_1_mission_3') && result === 'win' && !tutorialRewardChoice && !rewardsLoading && rewards.length === 0 && rewardsGranted) ||
        (isCampaign && isStrategyRewardMission && result === 'win' && (m2StrategyGranted || (m2StrategyChoices && m2StrategyChoices.length === 0))) ||
        (isCampaignTutorial && result === 'win' && tutorialRewardGranted)
      ) && (
      <div className="afterbattle-action-buttons">
        {isCampaignTutorial && (missionId === 'chapter_1_mission_1' || missionId === 'chapter_1_mission_3') ? (
          <>
            {missionId !== 'chapter_1_mission_3' && (
              <AfterBattleButton
                variant="golden"
                onClick={() => navigate('/campaign-battle/chapter_1_mission_2')}
              >
                {t('afterBattle.nextTutorialMission')}
              </AfterBattleButton>
            )}
            <AfterBattleButton
              variant={missionId === 'chapter_1_mission_3' ? 'golden' : 'white'}
              onClick={() => goToCampaignTutorialHub()}
            >
              {t('afterBattle.backToTutorial')}
            </AfterBattleButton>
          </>
        ) : (
          <>
            <AfterBattleButton
              variant="white"
              style={{
                opacity:
                  isExploreUnknownRun &&
                  result === 'win' &&
                  !isExploreUnknownHomecoming &&
                  !exploreUnknownVictoryFlowDone
                    ? 0.45
                    : 1,
              }}
              disabled={
                isExploreUnknownRun &&
                result === 'win' &&
                !isExploreUnknownHomecoming &&
                !exploreUnknownVictoryFlowDone
              }
              onClick={async () => {
              requestPendingSeasonEndCheck();
              if (isCampaignTutorial) {
                goToCampaignTutorialHub();
                return;
              }
              if (isExploreUnknownRun) {
                if (isExploreUnknownHomecoming) {
                  navigate('/battleoverview', { replace: true });
                  return;
                }
                const uid = localStorage.getItem('userId');
                const numUid = uid != null ? parseInt(uid, 10) : NaN;
                const strategyFromState =
                  (uid && gameState?.state?.[uid]?.strategy?.id) ??
                  (!Number.isNaN(numUid) && gameState?.state?.[numUid]?.strategy?.id) ??
                  (uid && gameState?.state?.[String(uid)]?.strategy?.id) ??
                  location.state?.strategyId;
                if (result === 'win') {
                  if (strategyFromState == null || strategyFromState === '') {
                    throw new Error('AfterBattle: Explore Unknown win missing player strategy id on game state');
                  }
                  const skipResumeSurface = gameState?.exploreUnknownSkipWorldResumeSurface;
                  const missionIndexForResume = Number(gameState?.exploreUnknownMissionIndex);
                  const resumeSurfaceFromMission =
                    !skipResumeSurface &&
                    Number.isInteger(missionIndexForResume) &&
                    missionIndexForResume > 0
                      ? getSurfaceForMissionIndex(missionIndexForResume)
                      : null;
                  const returnToExploreEndAfterHumanityWin =
                    gameState?.exploreUnknownReturnToExploreEnd === true ||
                    (Number(gameState?.exploreUnknownMissionIndex) === 75 &&
                      gameState?.exploreUnknownNegaraPeacefulWin !== true);
                  navigate('/explore-unknown', {
                    replace: true,
                    state: {
                      resumeArrival: true,
                      exploreUnknownCommittedStrategyId: String(strategyFromState),
                      exploreUnknownRunId: gameState?.exploreUnknownRunId,
                      exploreUnknownMapRefresh: true,
                      ...(skipResumeSurface
                        ? { exploreUnknownSkipResumeTargetSurface: String(skipResumeSurface) }
                        : resumeSurfaceFromMission
                          ? { exploreUnknownResumeSurface: resumeSurfaceFromMission }
                          : {}),
                      ...(gameState?.exploreUnknownChainImmediateBossMissionIndex != null
                        ? {
                            exploreUnknownChainImmediateBossMissionIndex: Number(
                              gameState.exploreUnknownChainImmediateBossMissionIndex
                            ),
                          }
                        : {}),
                      ...(gameState?.exploreUnknownExploreEnd === true
                        ? { exploreUnknownExploreEnd: true }
                        : {}),
                      ...(returnToExploreEndAfterHumanityWin
                        ? { exploreUnknownReturnToExploreEnd: true }
                        : {}),
                    },
                  });
                  return;
                }
                navigate('/battleoverview', { replace: true });
                return;
              }
              // Dismiss for any mission-ending AfterBattle (away redirect OR HQ conquer from BattleUI).
              // Previously only fromWarEffortMissionOver dismissed — HQ winners saw AfterBattle twice.
              if (isWarEffort && isWarEffortHQEnding && missionIdForDismiss) {
                addDismissedWarEffortMissionId(missionIdForDismiss);
                if (gameId) {
                  markWarEffortMissionOverMessageShown(gameId);
                }
                try {
                  const token = localStorage.getItem('token');
                  if (token) {
                    // Grant rewards first if not yet granted (e.g. player was not present when battle ended; ensures we don't leave without claiming)
                    if (gameId) {
                      try {
                        await fetch(`${API_URL}/battle-rewards/${gameId}/grant`, {
                          method: 'POST',
                          headers: { Authorization: `Bearer ${token}` }
                        });
                      } catch (grantErr) {
                        console.error('Failed to grant battle rewards before dismiss:', grantErr);
                      }
                    }
                    const res = await fetch(`${API_URL}/war-effort/mission/${missionIdForDismiss}/dismiss-completion`, {
                      method: 'POST',
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    if (res.ok) {
                      navigate('/wareffort', { replace: true, state: { dismissedMissionId: missionIdForDismiss } });
                      return;
                    }
                    console.error('Dismiss completion failed:', res.status);
                  }
                } catch (e) {
                  console.error('Failed to dismiss mission completion:', e);
                }
                // On failure, still navigate so user is not stuck; War Effort will treat dismissedMissionId and clear mission
                navigate('/wareffort', { replace: true, state: { dismissedMissionId: missionIdForDismiss } });
                return;
              }
              navigate(isWarEffort ? '/wareffort' : '/battleoverview');
            }}
            >
              {isCampaignTutorial
                ? t('afterBattle.backToTutorial')
                : isExploreUnknownHomecoming || (isExploreUnknownRun && result === 'loss')
                  ? t('afterBattle.backToBattleOverview')
                  : isExploreUnknownRun
                    ? result === 'win'
                      ? t('afterBattle.backToExploreUnknownMap')
                      : t('afterBattle.backToBattleOverview')
                    : (isWarEffort ? t('afterBattle.backToWarEffort') : t('afterBattle.backToBattleOverview'))}
            </AfterBattleButton>
            {opponentStrategy && (
              <AfterBattleButton
                variant="green"
                onClick={handleSubscribe}
                disabled={isSubscribing}
              >
                {isSubscribing ? t('afterBattle.subscribing') : t('afterBattle.subscribe')}
              </AfterBattleButton>
            )}
            {waitingForRematchOnThisGame && (
              <AfterBattleButton
                variant="white"
                onClick={() => rematch.cancelRematch()}
              >
                {t('rematch.cancelRequest')}
              </AfterBattleButton>
            )}
            {showPvpRematchButtons && !waitingForRematchOnThisGame && (
              <>
                <AfterBattleButton
                  variant="blue"
                  onClick={() => rematch.requestRematch(gameId, 'standard')}
                >
                  {t('rematch.requestRematch')}
                </AfterBattleButton>
                <AfterBattleButton
                  variant="blue"
                  onClick={() => rematch.requestRematch(gameId, 'mirror')}
                >
                  {t('rematch.requestMirrorRematch')}
                </AfterBattleButton>
              </>
            )}
            {isExploreUnknownRunEndSummary && (
              <AfterBattleButton
                variant="golden"
                onClick={() => {
                  navigate('/honor-hall', {
                    replace: true,
                    state: {
                      honorHallTab: 'explorers',
                      honorHallShowUserRank: true,
                    },
                  });
                }}
              >
                {t('afterBattle.viewHonorHallExplorersRank')}
              </AfterBattleButton>
            )}
          </>
        )}
      </div>
      )}
      
        </div>
      </div>
    </div>
  );
}

export default AfterBattle;
